// ============================================================================
// StudentPortal.js — BragaBJJ Student Dashboard
// Wallet-authenticated student view with profile, attendance, payments, store
// Mobile-optimized with responsive detection
// ============================================================================
import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import './StudentPortal.css';
import { loadDataFromServer, apiFetch, getApiUrl } from '../utils/serverConfig';
import StoreOrder from './StoreOrder';
import StudentBadgesTab from './StudentBadgesTab';
import StudentPurchaseHistory from './StudentPurchaseHistory';
import StudentCollectables from './StudentCollectables';
import CheckoutForm from './CheckoutForm';

// IPFS gateway used for rendering token images. Change here if you need a different gateway.
const IPFS_GATEWAY = process.env.REACT_APP_IPFS_GATEWAY || 'https://gateway.pinata.cloud';

// Recurring Stripe plans – must match Stripe metadata app_plan_id AND backend mapping
const RECURRING_PLANS = [
  {
    id: "weekly_adult_39",
    label: "Weekly Adult",
    description: "Billed every week",
    priceDisplay: "$39 per week",
  },
  {
    id: "weekly_kids_29",
    label: "Weekly Kids",
    description: "Billed every week (kids)",
    priceDisplay: "$29 per week",
  },
  {
    id: "biweekly_78",
    label: "Biweekly Membership",
    description: "Billed every 2 weeks",
    priceDisplay: "$78 every 2 weeks",
  },
  {
    id: "monthly_139",
    label: "Monthly Membership",
    description: "Billed every month",
    priceDisplay: "$139 per month",
  },
  {
    id: "three_month_349",
    label: "3-Month Membership",
    description: "Billed every 3 months",
    priceDisplay: "$349 every 3 months",
  },
  {
    id: "six_month_679",
    label: "6-Month Membership",
    description: "Billed every 6 months",
    priceDisplay: "$679 every 6 months",
  },
];

// Call your backend to create a Stripe Checkout Session
async function createRecurringCheckoutSession(planId) {
  const url = getApiUrl('/api/stripe/create-checkout-session');
  const res = await fetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ planId })
  });

  if (!res.ok) {
    const text = await res.text().catch(() => null);
    console.error('createRecurringCheckoutSession failed', res.status, text);
    throw new Error('Failed to create checkout session');
  }

  const data = await res.json().catch(() => null);
  if (!data) throw new Error('Empty response creating checkout session');

  if (data.url) {
    window.location.href = data.url;
    return;
  } else if (data.sessionId && window.Stripe) {
    const pub = process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY || window?.BRAGA_STRIPE_PUBLISHABLE || '';
    const stripe = window.Stripe(pub || '');
    if (!stripe) throw new Error('Stripe.js not available');
    await stripe.redirectToCheckout({ sessionId: data.sessionId });
    return;
  }

  throw new Error('Missing Stripe URL or sessionId in response');
}

// OccurringPaymentsTab component (top-level so it can be used in JSX below)
function OccurringPaymentsTab({ onOpenCheckout }) {
  const [selectedPlanIdLocal, setSelectedPlanIdLocal] = React.useState(null);
  const [isSubmittingLocal, setIsSubmittingLocal] = React.useState(false);
  const [acceptContractLocal, setAcceptContractLocal] = React.useState(false);
  const [errorLocal, setErrorLocal] = React.useState('');

  const handleStartPaymentLocal = async () => {
    if (!selectedPlanIdLocal) {
      setErrorLocal('Please select a membership option.');
      return;
    }
    if (!acceptContractLocal) {
      setErrorLocal('You must agree to the contract terms before continuing.');
      return;
    }
    setErrorLocal('');

    // If an onOpenCheckout handler is provided (wired to CheckoutForm), open that UI instead of redirecting immediately
    if (typeof onOpenCheckout === 'function') {
      try {
        onOpenCheckout(selectedPlanIdLocal);
        return;
      } catch (e) {
        console.warn('onOpenCheckout handler failed, falling back to direct Checkout session', e);
      }
    }

    setIsSubmittingLocal(true);
    try {
      await createRecurringCheckoutSession(selectedPlanIdLocal);
    } catch (err) {
      console.error(err);
      setErrorLocal('Something went wrong starting your payment. Please try again or contact the school.');
      setIsSubmittingLocal(false);
    }
  };

  return (
    <div className="occurring-payments-tab">
      <h2 className="tab-title">Occurring Payments</h2>
      <p className="tab-subtitle">Choose your membership option below. Payments will run automatically on a recurring basis.</p>

      <div className="plans-grid">
        {RECURRING_PLANS.map((plan) => (
          <div
            key={plan.id}
            role="button"
            tabIndex={0}
            aria-pressed={selectedPlanIdLocal === plan.id}
            className={"plan-card" + (selectedPlanIdLocal === plan.id ? ' selected' : '')}
            onClick={() => setSelectedPlanIdLocal(plan.id)}
            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setSelectedPlanIdLocal(plan.id); } }}
          >
            <div className="plan-header">
              <h3>{plan.label}</h3>
              <p className="plan-price">{plan.priceDisplay}</p>
            </div>
            <p className="plan-description">{plan.description}</p>
            {selectedPlanIdLocal === plan.id && (<div className="plan-selected-tag">Selected</div>)}
          </div>
        ))}
      </div>

      <div className="contract-box">
        <label className="contract-checkbox">
          <input type="checkbox" checked={acceptContractLocal} onChange={(e) => setAcceptContractLocal(e.target.checked)} />
          <span>I understand this is a continuous recurring payment. The only way to stop or cancel this payment is to communicate directly with the school. Cancellations must be requested at least 7 days before the next payment is processed.</span>
        </label>
        <p className="contract-note">By continuing, you agree that charges will continue automatically according to the selected plan until you request cancellation with the school.</p>
      </div>

      {errorLocal && <div className="error-message">{errorLocal}</div>}

      <button className="primary-button" onClick={handleStartPaymentLocal} disabled={!selectedPlanIdLocal || !acceptContractLocal || isSubmittingLocal}>
        {isSubmittingLocal ? 'Redirecting to secure payment...' : 'Continue to Secure Payment'}
      </button>
    </div>
  );
}

export default function StudentPortal({ walletAddress, onClose = () => {}, isAdmin: isAdminProp = null, currentStudentId: currentStudentIdProp = null }) {
  const [student, setStudent] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [allStudentsWithWallet, setAllStudentsWithWallet] = useState([]);
  const [selectedStudentId, setSelectedStudentId] = useState(null);
  const [attendance, setAttendance] = useState([]);
  const [messages, setMessages] = useState([]);
  const [activeTab, setActiveTab] = useState('profile');
  const [loading, setLoading] = useState(true);
  const [newMessage, setNewMessage] = useState('');
  const [sendingMessage, setSendingMessage] = useState(false);
  const [flowchartData, setFlowchartData] = useState(() => {
    try {
      const saved = localStorage.getItem(`flowchart_${walletAddress}`);
      return saved ? JSON.parse(saved) : getDefaultFlowchartData();
    } catch (e) {
      return getDefaultFlowchartData();
    }
  });
  
  // Mobile detection hook
  const [isMobile, setIsMobile] = useState(false);
  const [showWalletList, setShowWalletList] = useState(false);
  // MetaMask / admin connection helpers
  const [connectingWallet, setConnectingWallet] = useState(false);
  const [adminWallets, setAdminWallets] = useState([]);
  const [adminStudentsMap, setAdminStudentsMap] = useState({});
  // Payment-related state
  const [paymentHistory, setPaymentHistory] = useState([]);
  const [loadingEthPrice, setLoadingEthPrice] = useState(false);
  const [ethPrice, setEthPrice] = useState(null);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState('weekly');
  const [paymentCurrency, setPaymentCurrency] = useState('USD');
  const [processingPayment, setProcessingPayment] = useState(false);
  const [customAmount, setCustomAmount] = useState(139);
  const [customPreset, setCustomPreset] = useState('other');
  const [showMobileConfirm, setShowMobileConfirm] = useState(false);
  const [mobileConfirmPlan, setMobileConfirmPlan] = useState('weekly');

  // Flowchart / editor state
  const [flowchartView, setFlowchartView] = useState('guard');
  const [editMode, setEditMode] = useState(false);
  const [selectedNode, setSelectedNode] = useState(null);
  const [draggedNode, setDraggedNode] = useState(null);
  const [dragStart, setDragStart] = useState(null);
  const [flowZoom, setFlowZoom] = useState(1);
  const [flowPan, setFlowPan] = useState({ x: 0, y: 0 });
  const pinchRef = useRef(null);
  const touchPanRef = useRef(null);
  const nodeTouchRef = useRef(null);

  // Profile edit modal state
  const [showEditModal, setShowEditModal] = useState(false);
  const [editedProfile, setEditedProfile] = useState({});
  const [savingProfile, setSavingProfile] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [studentsVersion, setStudentsVersion] = useState(0);
  // Store modal for mobile compact view
  const [showStoreModal, setShowStoreModal] = useState(false);

  // Quick-stats (no desktop tab selector)

  // Technique progression (guard, pass, takedown) persisted per-student
  const [techProgress, setTechProgress] = useState({ guard: 0, pass: 0, takedown: 0 });
  const [techHistory, setTechHistory] = useState([]); // array of { date, guard, pass, takedown }
  const [progressSyncing, setProgressSyncing] = useState(false);

  const PROG_TARGET = 100; // default target for progression bars
  const ATTENDANCE_TARGET = 144; // classes needed to reach belt milestone (attendance target)
  const [beltCelebratedShown, setBeltCelebratedShown] = useState(false);
  const [, setShowAwardModal] = useState(false);
  const [, setMintingNFT] = useState(false);
  const [, setMintResult] = useState(null);

  const todayDateStr = () => {
    const d = new Date();
    return d.toISOString().slice(0, 10);
  };

  // Close handler which also navigates the dashboard back to the home view
  const handleClose = useCallback(() => {
    try {
      // ask the app to navigate back to the home/main menu
      try { window.dispatchEvent(new CustomEvent('navigateTo', { detail: { view: 'home', source: 'studentportal' } })); } catch (e) { /* no-op */ }
    } finally {
      // call provided onClose prop to allow parent to also hide the portal
      try { onClose(); } catch (e) { /* no-op */ }
    }
  }, [onClose]);

  // Programmatic opener for other parts of the app: open student portal for id (or current)
  const openStudentPortal = useCallback((studentId = null) => {
    try {
      window.dispatchEvent(new CustomEvent('navigateTo', { detail: { view: 'studentportal', currentStudentId: studentId, source: 'openStudentPortal' } }));
    } catch (e) {
      console.warn('openStudentPortal failed', e);
    }
  }, []);

  // Request wallet connection (mobile-friendly). If a student is present, confirm match.
  // openWallet optionally accepts a pre-fetched address. If addrArg is provided, it will skip the request step.
  // Special privileged admin wallet (always grant full supervise access)
  const SUPER_ADMIN_WALLET = '0x8bfc802e8decc49b63158b9ec4122e04a22d819c';

  // Admin badge definitions (visual + metadata only)
  const ADMIN_BADGES = {
    'coach': {
      id: 'coach',
      name: 'Coach',
      cid: 'bafybeicgupy7xm5qwcwjsaludeflb4krfqnsazii6irkabeumsaqt26kle',
      type: 'admin',
      borderClass: 'profile-coach'
    },
    'doctor-green': {
      id: 'doctor-green',
      name: 'Doctor Green',
      cid: 'bafybeiffwbyjqv2hmmhhp7l6zip34oendjhzucyfop74d5v6xtv5ofzbki',
      type: 'admin',
      borderClass: 'profile-doctor-green'
    },
    'baddie': {
      id: 'baddie',
      name: 'Baddie',
      cid: 'bafybeigkxcvchmzosn6sizba3c3kxv3imshqmqurcexmoqj3tndhut4joi',
      type: 'admin',
      borderClass: 'profile-baddie'
    }
    , 'super_baddie': {
      id: 'super_baddie',
      name: 'Super Baddie',
      cid: 'bafybeibwtpvmu4aazxzfzr6ix45nxyer5aydvxqvv7awpau4h2plqv7v2y',
      type: 'admin',
      borderClass: 'profile-super-baddie'
    }
    ,
    'master_class': {
      id: 'master_class',
      name: 'Master Class',
      cid: 'bafybeig5prrwvjk6si3ynml5uzqgszjqtgyj64ruxhyd2jsz65t3j5mn2e',
      type: 'admin',
      borderClass: 'profile-master-class'
    }
    ,'super_student': {
      id: 'super_student',
      name: 'Super Student',
      cid: 'bafybeibwmi6amxqwgr3xdztffxnlzgdimdagiur667kglu6ra3cqr2ymgi',
      type: 'admin',
      borderClass: 'profile-super-student'
    }
    , 'core_student': {
      id: 'core_student',
      name: 'Core Student',
      cid: 'bafybeialtr4hmf363sdz4tqxubxzty5cyz25m2mscuytpsidl6xncizlai',
      type: 'admin',
      borderClass: 'profile-core-student'
    }
    ,'upcoming_beast': {
      id: 'upcoming_beast',
      name: 'Upcoming Beast',
      cid: 'bafybeie2qli6pegdbbw6na4zklt7m2uctywam4hsqmhtbjc5hql2xv2h6y',
      type: 'admin',
      borderClass: 'profile-upcoming-beast'
    }
  };

  // Ensure this privileged wallet is only attached to Samuel Braga's profile locally
  const enforceSuperAdminMapping = (addr) => {
    try {
      if (!addr) return;
      const a = addr.toString().toLowerCase();
      if (a !== SUPER_ADMIN_WALLET) return;
      const raw = localStorage.getItem('students');
      const studs = raw ? JSON.parse(raw) : [];
      let changed = false;
      let samuel = studs.find(s => (s.id || '').toString().toLowerCase() === 'samuel-braga' || (s.id || '').toString().toLowerCase() === 'samuel_braga');
      // If samuel not present, try to find by name
      if (!samuel) samuel = studs.find(s => (s.name || '').toString().toLowerCase().includes('samuel'));
      const updated = (studs || []).map(s => {
        try {
          const w = (s.walletAddress || s.wallet || s.address || '').toString().toLowerCase();
          if (w === a && !(samuel && s.id === samuel.id)) {
            // remove wallet from other profiles
            changed = true;
            const copy = { ...s };
            delete copy.walletAddress;
            delete copy.wallet;
            return copy;
          }
          return s;
        } catch (e) { return s; }
      });

      if (samuel) {
        // ensure samuel has the privileged wallet
        if ((samuel.walletAddress || samuel.wallet || '').toString().toLowerCase() !== a) {
          changed = true;
          samuel.walletAddress = a;
          // reflect change in updated list
          for (let i = 0; i < updated.length; i++) if (updated[i].id === samuel.id) updated[i] = samuel;
        }
      }

      if (changed) {
        try { localStorage.setItem('students', JSON.stringify(updated)); } catch (e) { /* ignore */ }
        // update in-memory admin list/state
        const withWallet = updated.filter(s => (s.walletAddress || s.wallet || '').toString().toLowerCase());
        setAllStudentsWithWallet(withWallet.map(s => ({ ...s, walletAddress: (s.walletAddress || s.wallet || '').toString().toLowerCase() })));
        window.dispatchEvent(new CustomEvent('students-synced', { detail: { students: updated } }));
        if (samuel) window.dispatchEvent(new CustomEvent('student-updated', { detail: samuel }));
      }
    } catch (e) {
      console.warn('enforceSuperAdminMapping failed', e);
    }
  };

  const openWallet = useCallback(async (addrArg = null) => {
    if (!window?.ethereum) {
      alert('MetaMask not detected. Please install MetaMask to connect.');
      return null;
    }

    let addr = addrArg ? addrArg.toString().toLowerCase() : null;

    try {
      setConnectingWallet(true);
      if (!addr) {
        // If no address was provided, fall back to requesting accounts here (this path is kept for programmatic calls)
        const accounts = await window.ethereum?.request({ method: 'eth_requestAccounts' });
        addr = (accounts && accounts[0]) ? accounts[0].toString().toLowerCase() : null;
        if (!addr) {
          alert('No wallet address returned.');
          return null;
        }
      }

      // Try to find a matching student on the aggregated server first
      let students = [];
      try {
        const data = await loadDataFromServer();
        students = Array.isArray(data) ? data : (data && data.students ? data.students : []);
      } catch (e) {
        console.warn('openWallet: failed to load aggregated data, falling back to localStorage', e);
        try {
          const raw = localStorage.getItem('students');
          students = raw ? JSON.parse(raw) : [];
        } catch (ie) {
          students = [];
        }
      }

      // Normalize fields
      students = (students || []).map(s => ({ ...s, walletAddress: (s.walletAddress || s.wallet || s.wallet_address || s.address || '').toString() }));

      const matched = students.find(s => s.walletAddress && s.walletAddress.toLowerCase() === addr);
      if (matched) {
        // Found existing student linked to this wallet
        setStudent(matched);
        setSelectedStudentId(matched.id);
        // persist to local cache
        try {
          const raw = localStorage.getItem('students');
          const studs = raw ? JSON.parse(raw) : [];
          const merged = mergeStudentsArray(studs, students);
          localStorage.setItem('students', JSON.stringify(merged));
        } catch (e) { /* ignore */ }

        alert(`Wallet connected and matched to student: ${matched.name || (matched.firstName + ' ' + (matched.lastName || '')).trim() || matched.id}`);
        window.dispatchEvent(new CustomEvent('student-updated', { detail: matched }));
        // If this address is the privileged super-admin, enforce mapping so it remains admin-only
        try { enforceSuperAdminMapping(addr); } catch (e) { /* ignore */ }
        return addr;
      }

      // Not matched — offer to search by name on the server list or create/join
      const nameQuery = window.prompt('No student profile is linked to this wallet. Enter your full name to search existing profiles (Cancel to skip):');
      if (nameQuery && nameQuery.trim()) {
        const q = nameQuery.trim().toLowerCase();
        const matches = students.filter(s => {
          const full = (s.name || `${s.firstName || ''} ${s.lastName || ''}`).toString().toLowerCase();
          return full.includes(q) || (s.id || '').toString().toLowerCase().includes(q);
        });

        if (matches.length === 0) {
          const create = window.confirm('No matching profiles found. Create a new student profile and link this wallet?');
          if (create) {
            await createAndLinkNewStudent(addr);
            return addr;
          }
          return addr;
        }

        // Build a choice list
        const list = matches.map((m, i) => `${i + 1}) ${m.name || `${m.firstName || ''} ${m.lastName || ''}`.trim() || m.id}`).join('\n');
        const pick = window.prompt(`Found ${matches.length} matches:\n${list}\n\nEnter the number of the matching profile to link this wallet, or Cancel to skip:`);
        if (pick) {
          const idx = parseInt(pick, 10) - 1;
          if (!isNaN(idx) && matches[idx]) {
            const chosen = matches[idx];
            const ok = window.confirm(`Link wallet ${addr} to profile ${chosen.name || chosen.id}?`);
            if (ok) {
              try {
                const updated = { ...chosen, walletAddress: addr };
                // Update localStorage
                const raw = localStorage.getItem('students');
                const studs = raw ? JSON.parse(raw) : [];
                const updatedList = (studs.length ? studs : students).map(s => s.id === updated.id ? updated : s);
                localStorage.setItem('students', JSON.stringify(updatedList));
                setStudent(updated);
                setSelectedStudentId(updated.id);
                window.dispatchEvent(new CustomEvent('students-synced', { detail: { students: updatedList } }));
                window.dispatchEvent(new CustomEvent('student-updated', { detail: updated }));

                // Best-effort API persist
                try {
                  await apiFetch('/api/students/update', {
                    method: 'POST',
                    body: JSON.stringify(updated)
                  });
                } catch (err) {
                  console.warn('Failed to persist linked wallet to API (best-effort):', err);
                }

                alert('Wallet linked to profile. You now have full access to this student profile.');
              } catch (err) {
                console.error('Failed to link wallet to profile:', err);
                alert('Failed to link wallet. See console for details.');
              }
            }
          }
        }

        return addr;
      }

      // If we reach here user cancelled search — offer to create new student
      const createNew = window.confirm('Do you want to create a new student profile and link this wallet to it?');
      if (createNew) {
        await createAndLinkNewStudent(addr);
      }

      // Ensure super-admin mapping when creating or linking on mobile
      try { enforceSuperAdminMapping(addr); } catch (e) { /* ignore */ }

      return addr;
    } catch (e) {
      console.error('openWallet failed', e);
      if (e?.code === 4001) {
        alert('Wallet connection cancelled by user.');
      } else {
        alert('Wallet connection failed: ' + (e?.message || e));
      }
      return null;
    } finally {
      setConnectingWallet(false);
    }
  }, []);

  // Request accounts directly and then call openWallet with the returned address.
  const requestAndOpen = useCallback(async () => {
    if (!window?.ethereum) {
      alert('MetaMask not detected. Please install MetaMask to connect.');
      return null;
    }

    try {
      setConnectingWallet(true);
      const accounts = await window.ethereum?.request({ method: 'eth_requestAccounts' });
      const addr = (accounts && accounts[0]) ? accounts[0].toString().toLowerCase() : null;
      if (!addr) {
        alert('No wallet address returned.');
        return null;
      }
      // delegate to openWallet with the fetched address to run matching/linking logic
      return await openWallet(addr);
    } catch (e) {
      console.error('requestAndOpen failed', e);
      if (e?.code === 4001) {
        alert('Wallet connection cancelled by user.');
      } else {
        alert('Wallet connection failed: ' + (e?.message || e));
      }
      return null;
    } finally {
      setConnectingWallet(false);
    }
  }, [openWallet]);

  // Helper: merge two student arrays by id (prefer fetched entries)
  function mergeStudentsArray(localArr = [], fetchedArr = []) {
    try {
      const map = {};
      (fetchedArr || []).forEach(s => { if (s && s.id) map[s.id] = s; });
      (localArr || []).forEach(s => { if (s && s.id && !map[s.id]) map[s.id] = s; });
      return Object.keys(map).map(k => map[k]);
    } catch (e) {
      return fetchedArr && fetchedArr.length ? fetchedArr : localArr;
    }
  }

  // Helper: create a minimal new student profile, link wallet, persist locally and try API persist
  async function createAndLinkNewStudent(addr) {
    try {
      const newId = `student_${Date.now()}`;
      const newStudent = {
        id: newId,
        walletAddress: (addr || '').toString(),
        joinDate: new Date().toISOString(),
        name: 'New Student',
        firstName: 'New',
        lastName: 'Student'
      };

      const raw = localStorage.getItem('students');
      const studs = raw ? JSON.parse(raw) : [];
      studs.push(newStudent);
      localStorage.setItem('students', JSON.stringify(studs));
      setStudent(newStudent);
      setSelectedStudentId(newId);

      window.dispatchEvent(new CustomEvent('students-synced', { detail: { students: studs } }));
      window.dispatchEvent(new CustomEvent('student-updated', { detail: newStudent }));

      try {
        await apiFetch('/api/students', {
          method: 'POST',
          body: JSON.stringify(newStudent)
        });
      } catch (err) {
        console.warn('createAndLinkNewStudent: failed to POST new student to API (best-effort):', err);
      }

      alert('Joined the Student Portal as a new student. You can update your profile in Edit Profile.');
    } catch (e) {
      console.error('createAndLinkNewStudent failed', e);
      alert('Failed to create new student profile.');
    }
  }

  // Robust fetch helper for aggregated /api/data with retries and fallbacks
  async function fetchAggregatedData({ force = false, retries = 3, backoff = 600 } = {}) {
    let attempt = 0;
    const errors = [];

    while (attempt < retries) {
      attempt += 1;
      try {
        // Prefer centralized loader helper which may respect DEV ports
        try {
          const data = await loadDataFromServer();
          if (data) return data;
        } catch (e) {
          // if loadDataFromServer failed, we'll try direct network fetch below
          errors.push(e?.message || String(e));
        }

        // Try direct fetch to /api/data (relative) which should reach aggregated backend when proxied
        try {
          const url = getApiUrl ? (getApiUrl('/api/data')) : '/api/data';
          const res = await fetch(url + (force ? '?force=1' : ''), { headers: { 'Content-Type': 'application/json' } });
          if (res && res.ok) {
            const d = await res.json();
            return d;
          } else {
            errors.push(`fetch status ${res && res.status}`);
          }
        } catch (e) {
          errors.push(e?.message || String(e));
        }

      } catch (outer) {
        errors.push(outer?.message || String(outer));
      }

      // backoff before retry (capture current attempt so loop funcs don't close over a mutating var)
      const delay = backoff * attempt;
      await new Promise(r => setTimeout(r, delay));
    }

    console.warn('fetchAggregatedData failed after retries:', errors.slice(0, 3));
    return null;
  }

  // Expose programmatic helpers on window for integration/testing/debugging
  useEffect(() => {
    try {
      window.openStudentPortal = openStudentPortal;
      window.openWallet = requestAndOpen;
    } catch (e) {
      // ignore
    }
    return () => {
      try { delete window.openStudentPortal; } catch (e) {}
      try { delete window.openWallet; } catch (e) {}
    };
  }, [openStudentPortal, requestAndOpen]);

  // Quick test helper: open the flowchart editor programmatically
  useEffect(() => {
    try {
      window.openFlowchartEditor = () => {
        try { setActiveTab('flowchart'); } catch (e) {}
        try { setEditMode(true); } catch (e) {}
      };
    } catch (e) {
      // ignore
    }
    return () => {
      try { delete window.openFlowchartEditor; } catch (e) {}
    };
  }, []);
  
  // Load technique progress when student changes and provide update helpers
  useEffect(() => {
    if (!student || !student.id) return;
    let mounted = true;
    (async () => {
      try {
        let serverProgress = null;
        try {
          const res = await apiFetch(`/api/students/${encodeURIComponent(student.id)}/progress`);
          if (res && res.ok) serverProgress = await res.json().catch(() => null);
        } catch (e) {
          /* ignore server errors */
        }

        const raw = localStorage.getItem(`tech_progress_${student.id}`);
        const local = raw ? JSON.parse(raw) : null;

        const merged = { total: { guard: 0, pass: 0, takedown: 0 }, history: [] };
        if (local && local.total) merged.total = { ...merged.total, ...local.total };
        if (serverProgress && serverProgress.total) merged.total = { ...merged.total, ...serverProgress.total };
        if (local && Array.isArray(local.history)) merged.history = [...local.history];
        if (serverProgress && Array.isArray(serverProgress.history)) {
          const map = {};
          merged.history.forEach(h => { map[h.date] = { ...(map[h.date]||{}), ...h }; });
          serverProgress.history.forEach(h => { map[h.date] = { ...(map[h.date]||{}), ...h }; });
          merged.history = Object.keys(map).sort().map(d => map[d]);
        }

        if (!mounted) return;
        const t = merged.total || { guard: 0, pass: 0, takedown: 0 };
        setTechProgress({ guard: Number(t.guard || 0), pass: Number(t.pass || 0), takedown: Number(t.takedown || 0) });
        setTechHistory(Array.isArray(merged.history) ? merged.history : []);
      } catch (err) {
        console.warn('Failed to load technique progress', err);
      }
    })();
    return () => { mounted = false; };
  }, [student]);

  const persistProgress = async (newTotal, newHistory) => {
    if (!student || !student.id) return;
    const payload = { total: newTotal, history: newHistory };
    try {
      try { localStorage.setItem(`tech_progress_${student.id}`, JSON.stringify(payload)); } catch (e) { /* ignore */ }
      setProgressSyncing(true);
      try {
        await apiFetch(`/api/students/${encodeURIComponent(student.id)}/progress`, { method: 'POST', body: JSON.stringify(payload) });
      } catch (e) { /* ignore server errors */ }
    } finally {
      setProgressSyncing(false);
    }
  };

  const updateTodayTechnique = (tech, delta) => {
    if (!student || !student.id) return;
    const date = todayDateStr();
    const historyMap = (techHistory || []).slice();
    let today = historyMap.find(h => h.date === date);
    if (!today) { today = { date, guard: 0, pass: 0, takedown: 0 }; historyMap.push(today); }
    today[tech] = Math.max(0, (Number(today[tech] || 0) + Number(delta)));
    const totals = { guard: 0, pass: 0, takedown: 0 };
    historyMap.forEach(h => { totals.guard += Number(h.guard || 0); totals.pass += Number(h.pass || 0); totals.takedown += Number(h.takedown || 0); });
    setTechHistory(historyMap);
    setTechProgress(totals);
    persistProgress(totals, historyMap);
  };

  const resetProgress = () => {
    if (!student || !student.id) return;
    const empty = { guard: 0, pass: 0, takedown: 0 };
    setTechHistory([]);
    setTechProgress(empty);
    try { localStorage.removeItem(`tech_progress_${student.id}`); } catch (e) {}
    try { apiFetch(`/api/students/${encodeURIComponent(student.id)}/progress`, { method: 'POST', body: JSON.stringify({ total: empty, history: [] }) }); } catch (e) {}
  };

  // Check-in handler: record attendance locally, POST to API, and broadcast to other tabs (KioskMode / ControlCenter)
  const handleCheckIn = async () => {
    if (!student || !student.id) {
      alert('No student selected for check-in');
      return;
    }

    const timestamp = new Date().toISOString();

    // Update local storage (support both array-of-records and map formats)
    try {
      const raw = localStorage.getItem('attendance');
      if (!raw) {
        // store as array of records by default
        const arr = [{ studentId: student.id, timestamp }];
        localStorage.setItem('attendance', JSON.stringify(arr));
      } else {
        const parsed = JSON.parse(raw);
        if (Array.isArray(parsed)) {
          parsed.push({ studentId: student.id, timestamp });
          localStorage.setItem('attendance', JSON.stringify(parsed));
        } else if (typeof parsed === 'object') {
          parsed[student.id] = parsed[student.id] || [];
          parsed[student.id].push(timestamp);
          localStorage.setItem('attendance', JSON.stringify(parsed));
        }
      }
    } catch (e) {
      console.warn('Failed to persist attendance locally', e);
    }

    // Update local React state
    try {
      setAttendance(prev => (Array.isArray(prev) ? [...prev, timestamp] : [timestamp]));
    } catch (e) { /* ignore */ }

    // Broadcast cross-tab via CustomEvent and BroadcastChannel
    try {
      window.dispatchEvent(new CustomEvent('attendance-updated', { detail: { studentId: student.id, timestamp } }));
      window.dispatchEvent(new CustomEvent('kiosk-checkin', { detail: { studentId: student.id, timestamp } }));
      try {
        const bc = new BroadcastChannel('braga-sync');
        bc.postMessage({ type: 'attendance', studentId: student.id, timestamp });
        bc.close();
      } catch (e) {
        // BroadcastChannel not available in all environments
      }
      // also nudge other tabs via a storage toggle
      try { localStorage.setItem('braga_last_checkin', JSON.stringify({ studentId: student.id, timestamp })); } catch (e) {}
    } catch (e) {
      // ignore
    }

    // Best-effort: POST to API
    try {
      await apiFetch('/api/attendance', {
        method: 'POST',
        body: JSON.stringify({ studentId: student.id, timestamp })
      });
    } catch (err) {
      console.warn('Failed to POST attendance to API (best-effort):', err);
    }

    alert('Checked in — see Attendance tab for your entry.');
  };

  const renderProgressControls = () => {
    const pct = (val) => Math.min(100, Math.round((val / PROG_TARGET) * 100));
    return (
      <div style={{ marginTop: 12 }} className="tech-progression">
        <h4 style={{ marginTop: 0 }}>Technique Progression</h4>
        <div style={{ display: 'grid', gap: 8 }}>
          {['guard','pass','takedown'].map((t) => (
            <div key={t} style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <div style={{ flex: 1 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <div style={{ fontWeight: 700, textTransform: 'capitalize' }}>{t.replace(/^(.)/, s => s.toUpperCase())}</div>
                  <div style={{ fontSize: 13, color: 'var(--portal-muted)' }}>{techProgress[t] || 0}/{PROG_TARGET}</div>
                </div>

                {/* Achievement badges every 20 points */}
                <div style={{ display: 'flex', gap: 6, marginTop: 6, alignItems: 'center' }}>
                  {Array.from({ length: Math.max(0, Math.floor((techProgress[t] || 0) / 20)) }).map((_, i) => (
                    <div key={`badge-${t}-${i}`} className="tech-badge" title={`Achievement ${(i+1)} (+20)`}>🏅</div>
                  ))}
                </div>

                <div className="progress-bar">
                  <div className="progress-bar-fill" style={{ width: `${pct(techProgress[t])}%` }} />
                </div>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                <button className="btn-sm" onClick={() => updateTodayTechnique(t, 1)}>+1</button>
                <button className="btn-sm" onClick={() => updateTodayTechnique(t, -1)}>-1</button>
              </div>
            </div>
          ))}
        </div>
        <div style={{ marginTop: 8, display: 'flex', gap: 8, alignItems: 'center' }}>
          <button className="btn-sm" onClick={() => resetProgress()}>Reset Progress</button>
          <div style={{ fontSize: 13, color: 'var(--portal-muted)' }}>{progressSyncing ? 'Syncing…' : 'Progress saved locally'}</div>
        </div>
      </div>
    );
  };

  const renderTrophies = () => {
    // For each technique, compute how many 20-point trophies unlocked
    const perTech = ['guard','pass','takedown'].map(t => ({
      name: t,
      count: Math.floor((techProgress[t] || 0) / 20)
    }));

    const total = perTech.reduce((s, p) => s + p.count, 0);

    return (
      <div className="trophy-area" style={{ marginBottom: 12 }}>
        <div className="trophy-header">
          <div className="trophy-title">Trophies</div>
          <div className="trophy-sub">{total} unlocked</div>
        </div>

        <div className="trophy-list" style={{ display: 'flex', gap: 8, marginTop: 8, flexWrap: 'wrap' }}>
          {perTech.map(p => (
            <div key={`trophy-${p.name}`} className="trophy-chip" title={`${p.count} trophies for ${p.name}`}>
              <div className="trophy-icon">🏆</div>
              <div className="trophy-meta">
                <div className="trophy-name">{p.name.replace(/^(.)/, s => s.toUpperCase())}</div>
                <div className="trophy-count">{p.count}</div>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  // Persist progress on page unload to reduce lost work
  useEffect(() => {
    const handler = (ev) => {
      try {
        if (!student || !student.id) return;
        // synchronous local save
        try { localStorage.setItem(`tech_progress_${student.id}`, JSON.stringify({ total: techProgress, history: techHistory })); } catch (e) {}
        // best-effort async API save (fire-and-forget)
        try { apiFetch(`/api/students/${encodeURIComponent(student.id)}/progress`, { method: 'POST', body: JSON.stringify({ total: techProgress, history: techHistory }) }); } catch (e) {}
      } catch (e) {
        // ignore
      }
    };

    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [student, techProgress, techHistory]);

  // Helper: read attendance for a specific student from localStorage (supports array or map formats)
  const readAttendanceFromLocal = (studentId) => {
    if (!studentId) return [];
    try {
      const raw = localStorage.getItem('attendance');
      if (!raw) return [];
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed)) {
        return parsed.filter(a => (a.studentId || a.id) === studentId).map(a => a.timestamp || a.ts || a.date || a);
      } else if (typeof parsed === 'object') {
        return parsed[studentId] ? (parsed[studentId].map(t => t)) : [];
      }
    } catch (e) {
      console.warn('readAttendanceFromLocal failed', e);
    }
    return [];
  };

  // Listen for attendance updates from other tabs (CustomEvent, BroadcastChannel, storage) and refresh local state
  useEffect(() => {
    if (!selectedStudentId) return;

    const refreshFromLocal = () => {
      try {
        const local = readAttendanceFromLocal(selectedStudentId) || [];
        setAttendance(Array.isArray(local) ? local : []);
      } catch (e) {
        console.warn('Failed to refresh attendance from local', e);
      }
    };

    const onAttendanceEvent = (e) => {
      try {
        const sid = e?.detail?.studentId || (e?.studentId) || null;
        if (!sid) return;
        if (sid === selectedStudentId) refreshFromLocal();
      } catch (err) {
        console.warn('attendance event handler error', err);
      }
    };

    const onStorage = (ev) => {
      try {
        if (!ev) return;
        if (ev.key === 'braga_last_checkin') {
          const val = ev.newValue ? JSON.parse(ev.newValue) : null;
          if (val && val.studentId === selectedStudentId) refreshFromLocal();
        }
      } catch (e) { /* ignore */ }
    };

    window.addEventListener('attendance-updated', onAttendanceEvent);
    window.addEventListener('kiosk-checkin', onAttendanceEvent);
    window.addEventListener('storage', onStorage);

    // BroadcastChannel fallback
    let bc = null;
    try {
      bc = new BroadcastChannel('braga-sync');
      bc.onmessage = (m) => {
        try {
          if (m && m.data && m.data.type === 'attendance' && m.data.studentId === selectedStudentId) {
            refreshFromLocal();
          }
        } catch (e) { /* ignore */ }
      };
    } catch (e) {
      bc = null;
    }

    return () => {
      window.removeEventListener('attendance-updated', onAttendanceEvent);
      window.removeEventListener('kiosk-checkin', onAttendanceEvent);
      window.removeEventListener('storage', onStorage);
      try { if (bc) { bc.close(); } } catch (e) {}
    };
  }, [selectedStudentId, allStudentsWithWallet]);

  // Calculate stats (moved earlier so hooks/useEffects that reference `stats` don't hit TDZ)
  /* eslint-disable no-undef */
  const stats = useMemo(() => {
    if (!student || !attendance) {
      return {
        totalClasses: 0,
        thisMonth: 0,
        thisWeek: 0,
        streak: 0,
        lastAttendance: null,
        paymentStatus: 'unknown',
        daysOverdue: 0
      };
    }

    const now = new Date();
    // compute start of current week and month for attendance calculations
    const startOfWeek = new Date(now);
    startOfWeek.setDate(now.getDate() - now.getDay());
    startOfWeek.setHours(0, 0, 0, 0);
    const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
    startOfMonth.setHours(0, 0, 0, 0);

    const attendanceDates = attendance.map(ts => new Date(ts)).sort((a, b) => b - a);
    const thisMonth = attendanceDates.filter(d => d >= startOfMonth).length;
    const thisWeek = attendanceDates.filter(d => d >= startOfWeek).length;

    // Calculate streak
    let streak = 0;
    const sortedDates = attendanceDates.map(d => d.toDateString());
    const uniqueDates = [...new Set(sortedDates)];
    for (let i = 0; i < uniqueDates.length; i++) {
      const checkDate = new Date();
      checkDate.setDate(checkDate.getDate() - i);
      if (uniqueDates.includes(checkDate.toDateString())) {
        streak++;
      } else {
        break;
      }
    }

    const lastPayment = student.lastPaymentDate ? new Date(student.lastPaymentDate) : null;
    const daysOverdue = lastPayment ? Math.floor((now - lastPayment) / (1000 * 60 * 60 * 24)) : 999;
    const paymentStatus = daysOverdue <= 7 ? 'paid' : daysOverdue <= 14 ? 'due' : 'overdue';

    return {
      totalClasses: attendance.length,
      thisMonth,
      thisWeek,
      streak,
      lastAttendance: attendanceDates[0] || null,
      paymentStatus,
      daysOverdue
    };
  }, [student, attendance]);
  /* eslint-enable no-undef */

  // Render attendance progress toward ATTENDANCE_TARGET
  const renderAttendanceProgress = () => {
    const total = stats?.totalClasses || 0;
    const pct = Math.min(100, Math.round((total / ATTENDANCE_TARGET) * 100));
    const remaining = Math.max(0, ATTENDANCE_TARGET - total);
    return (
      <div className="attendance-progress" style={{ marginBottom: 12 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div style={{ fontWeight: 700 }}>Progress to Belt Target</div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <div style={{ color: 'var(--portal-muted)', fontSize: 13 }}>{total}/{ATTENDANCE_TARGET} classes • {pct}%</div>
            {total >= ATTENDANCE_TARGET && (
              <div className="belt-attained-badge" title="Belt target reached!">🎉 BELT ATTAINED</div>
            )}
          </div>
        </div>
        <div className="progress-bar" style={{ height: 12, borderRadius: 8, marginTop: 8 }}>
          <div className="progress-bar-fill" style={{ width: `${pct}%` }} />
        </div>
        <div style={{ marginTop: 8, fontSize: 13, color: 'var(--portal-muted)' }}>{remaining} classes remaining to reach {ATTENDANCE_TARGET}</div>
      </div>
    );
  };

  // Confetti animation (lightweight) — appended to document body and auto-removed
  const triggerConfetti = (count = 60) => {
    try {
      const colors = ['#f59e0b','#ef4444','#10b981','#7c3aed','#22d3ee','#f97316'];
      const container = document.createElement('div');
      container.style.position = 'fixed';
      container.style.left = '0';
      container.style.top = '0';
      container.style.width = '100%';
      container.style.height = '0';
      container.style.pointerEvents = 'none';
      container.style.zIndex = '99999';
      document.body.appendChild(container);
      for (let i = 0; i < count; i++) {
        const el = document.createElement('div');
        const size = Math.floor(Math.random() * 10) + 6;
        el.style.position = 'absolute';
        el.style.left = Math.floor(Math.random() * 100) + '%';
        el.style.top = '-10px';
        el.style.width = `${size}px`;
        el.style.height = `${size * 0.6}px`;
        el.style.background = colors[Math.floor(Math.random() * colors.length)];
        el.style.opacity = String(0.9 - Math.random() * 0.4);
        el.style.borderRadius = `${Math.random() > 0.5 ? '3px' : '50%'}`;
        el.style.transform = `rotate(${Math.floor(Math.random() * 360)}deg)`;
        el.style.transition = `transform 1.6s ease-out, top 1.6s cubic-bezier(.2,.8,.2,1), left 1.6s ease-out, opacity 0.6s ease`;
        container.appendChild(el);
        // Force layout then animate
        requestAnimationFrame(() => {
          const destY = window.innerHeight + 40 + Math.random() * 200;
          const destX = (parseFloat(el.style.left) + (Math.random() * 30 - 15));
          el.style.top = `${destY}px`;
          el.style.left = `${Math.max(0, Math.min(100, destX))}%`;
          el.style.opacity = '0';
        });
      }
      // remove after animation
      setTimeout(() => { try { document.body.removeChild(container); } catch (e) {} }, 2200);
    } catch (e) { console.warn('confetti failed', e); }
  };

  // Mint a trophy NFT via server endpoint (best-effort), fallback to client alert
  /* eslint-disable-next-line no-unused-vars */
  const mintTrophyNFT = async () => {
    if (!student || !student.id) return alert('No student selected');
    setMintingNFT(true);
    setMintResult(null);
    try {
      const payload = { studentId: student.id, type: 'belt', classes: stats.totalClasses, timestamp: new Date().toISOString() };
      try {
        const res = await apiFetch('/api/nft/mint', { method: 'POST', body: JSON.stringify(payload) });
        const json = await res.json().catch(() => null);
        if (res && res.ok) {
          setMintResult({ success: true, data: json });
          alert('🎉 NFT mint requested. Check server logs or student profile for claim link.');
        } else {
          console.warn('Server mint failed', json);
          // fallback: ask server to queue mint and return a message
          setMintResult({ success: false, error: json || 'Server failed' });
          alert('NFT mint request failed. See console for details.');
        }
      } catch (err) {
        console.warn('api/nft/mint failed', err);
        setMintResult({ success: false, error: String(err) });
        alert('NFT mint request failed (network).');
      }
    } finally {
      setMintingNFT(false);
    }
  };

  // Detect belt attainment transition and trigger celebration
  useEffect(() => {
    try {
      const total = stats?.totalClasses || 0;
      if (total >= ATTENDANCE_TARGET && !beltCelebratedShown) {
        // first time reaching target in this session
        setBeltCelebratedShown(true);
        // small delay so UI updates then confetti
        setTimeout(() => {
          triggerConfetti(80);
          setShowAwardModal(true);
          try { window.dispatchEvent(new CustomEvent('belt-attained', { detail: { studentId: student?.id, total } })); } catch (e) {}
        }, 300);
      }
    } catch (e) {}
  }, [stats?.totalClasses, student?.id, beltCelebratedShown]);

  // Floating wallet connect indicator (clickable)
  const WalletFloating = () => {
    const connected = !!(student && (student.walletAddress || student.wallet));
    return (
      <button
        className={`wallet-floating ${connected ? 'connected' : 'disconnected'}`}
        onClick={async (e) => { e.stopPropagation(); try { await requestAndOpen(); } catch (err) { console.warn(err); } }}
        title={connected ? 'Wallet connected — click to re-confirm' : 'No wallet linked — click to connect'}
        aria-label="Wallet status"
      >
        <span className="wf-icon">{connected ? '🔗' : '❌'}</span>
      </button>
    );
  };
  useEffect(() => {
    const checkMobile = () => {
      const mobile = window.innerWidth <= 768 || 
                     /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
      setIsMobile(mobile);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Load payment history
  useEffect(() => {
    if (!student) return;
    try {
      const loadPayments = async () => {
        // Local first
        const stored = localStorage.getItem('studentPayments');
        const allPayments = stored ? JSON.parse(stored) : {};
        const studentPayments = allPayments[student.id] || [];

        // Best-effort: fetch server payments and merge
        try {
          const res = await apiFetch(`/api/payments?studentId=${encodeURIComponent(student.id)}`);
          if (res && res.ok) {
            const server = await res.json().catch(() => null) || [];
            // normalize server to array
            const serverArr = Array.isArray(server) ? server : (server.payments || server.records || []);

            // Merge by txHash or id, prefer server values when present
            const map = {};
            studentPayments.concat(serverArr).forEach(p => {
              const key = (p.txHash || p.id || JSON.stringify(p)).toString();
              map[key] = { ...(map[key] || {}), ...(p || {}) };
            });
            const merged = Object.keys(map).map(k => map[k]);
            // persist merged locally
            const mergedAll = { ...allPayments, [student.id]: merged };
            try { localStorage.setItem('studentPayments', JSON.stringify(mergedAll)); } catch (e) { /* ignore */ }
            setPaymentHistory(merged);
            return;
          }
        } catch (e) {
          console.warn('Failed to fetch payments from API (best-effort):', e);
        }

        // fallback: use local only
        setPaymentHistory(studentPayments);
      };

      loadPayments();
    } catch (e) {
      console.error('Failed to load payment history:', e);
    }
  }, [student]);

  // Fetch ETH price on mount and when payment modal opens
  useEffect(() => {
    const fetchEthPrice = async () => {
      setLoadingEthPrice(true);
      try {
        // Try CoinGecko API first
        const response = await fetch('https://api.coingecko.com/api/v3/simple/price?ids=ethereum&vs_currencies=usd');
        const data = await response.json();
        const price = data?.ethereum?.usd;
        
        if (price) {
          setEthPrice(price);
          localStorage.setItem('ethPrice', JSON.stringify({ price, timestamp: Date.now() }));
        } else {
          throw new Error('Invalid price data');
        }
      } catch (e) {
        console.warn('Failed to fetch ETH price from CoinGecko, using fallback:', e);
        
        // Fallback: try cached price
        try {
          const cached = localStorage.getItem('ethPrice');
          if (cached) {
            const { price, timestamp } = JSON.parse(cached);
            // Use cached if less than 1 hour old
            if (Date.now() - timestamp < 3600000) {
              setEthPrice(price);
            } else {
              // Use default fallback price
              setEthPrice(2500); // Conservative ETH price estimate
            }
          } else {
            setEthPrice(2500);
          }
        } catch {
          setEthPrice(2500);
        }
      } finally {
        setLoadingEthPrice(false);
      }
    };

    fetchEthPrice();
    // Refresh price every 5 minutes
    const interval = setInterval(fetchEthPrice, 300000);
    return () => clearInterval(interval);
  }, []);

  // Payment reminder - check weekly
  useEffect(() => {
    if (!student) return;
    
    const checkPaymentReminder = () => {
      const lastPayment = student.lastPaymentDate ? new Date(student.lastPaymentDate) : null;
      const daysSincePayment = lastPayment ? Math.floor((new Date() - lastPayment) / (1000 * 60 * 60 * 24)) : 999;
      
      // Show reminder if payment is overdue (more than 7 days)
      if (daysSincePayment > 7 && window.Notification && Notification.permission === 'granted') {
        new Notification('BragaBJJ Payment Reminder', {
          body: `Your payment is ${daysSincePayment} days overdue. Please update your payment.`,
          icon: '/favicon.ico'
        });
      }
    };

    // Check on mount
    checkPaymentReminder();
    
    // Check weekly (every 7 days)
    const weeklyCheck = setInterval(checkPaymentReminder, 7 * 24 * 60 * 60 * 1000);
    
    return () => clearInterval(weeklyCheck);
  }, [student]);

  // Load student data from API
  useEffect(() => {
    if (!walletAddress) {
      setLoading(false);
      return;
    }

    const loadStudentData = async () => {
      try {
        // Check if admin wallet
        const adminWallet = localStorage.getItem('adminWallet');
        const isAdminWallet = (adminWallet && adminWallet.toLowerCase() === (walletAddress || '').toLowerCase()) || ((walletAddress || '').toString().toLowerCase() === SUPER_ADMIN_WALLET);
        setIsAdmin(Boolean(isAdminWallet));
        // Fetch students from aggregated endpoint with robust helper
        let students = [];
        try {
          const data = await fetchAggregatedData({ force: false, retries: 3 });
          students = Array.isArray(data) ? data : (data && data.students ? data.students : []);
          if (!students || students.length === 0) {
            // fallback to localStorage
            const cachedStudents = localStorage.getItem('students');
            if (cachedStudents) students = JSON.parse(cachedStudents);
          } else {
            try { localStorage.setItem('students', JSON.stringify(students)); } catch (e) { /* ignore */ }
          }
          console.log('Loaded students from aggregated endpoint (robust):', (students || []).length);
        } catch (e) {
          console.warn('loadStudentData: fetchAggregatedData failed, using localStorage fallback', e);
          const cachedStudents = localStorage.getItem('students');
          if (cachedStudents) students = JSON.parse(cachedStudents);
        }

        // If no students were returned, try a static data file as a last-resort
        if ((!students || students.length === 0)) {
          try {
            const fallback = await fetch('/data/students.json');
            if (fallback.ok) {
              const fallbackData = await fallback.json();
              if (Array.isArray(fallbackData) && fallbackData.length > 0) {
                students = fallbackData;
                localStorage.setItem('students', JSON.stringify(students));
                console.log('Loaded students from /data/students.json fallback:', students.length);
              }
            }
          } catch (e) {
            // ignore - fallback failed
            console.warn('Fallback /data/students.json fetch failed:', e);
          }
        }

        // Normalize wallet fields (some files use `wallet` or `wallet_address`)
        students = (students || []).map(s => {
          const wa = s.walletAddress || s.wallet || s.wallet_address || s.wallet_addr || s.walletaddr || s.address || '';
          return { ...s, walletAddress: wa };
        });

        if (isAdminWallet) {
          // Admin: show all students with wallet addresses
          const studentsWithWallet = students
            .map(s => ({ ...s, walletAddress: (s.walletAddress || s.wallet || s.wallet_address || s.address || '').toString().toLowerCase() }))
            .filter(s => s.walletAddress);

          setAllStudentsWithWallet(studentsWithWallet);

          // Build adminStudentsMap from any students marked isAdmin
          const adminMap = {};
          studentsWithWallet.forEach(s => { if (s.isAdmin) adminMap[s.walletAddress] = s; });
          setAdminStudentsMap(adminMap);

          // Voice feedback for admin
          if (window.speechSynthesis) {
            const utterance = new SpeechSynthesisUtterance('Access granted. Full access supreme.');
            utterance.rate = 1.1;
            utterance.pitch = 0.9;
            window.speechSynthesis.speak(utterance);
          }

          // If a seeded admin exists (samuel-braga), prefer that as default
          const seeded = studentsWithWallet.find(s => (s.id || '').toLowerCase() === 'samuel-braga' || (s.id || '').toLowerCase() === 'samuel_braga');
          if (seeded) {
            setSelectedStudentId(seeded.id);
            setStudent(seeded);
            setLoading(false);
            return;
          }

          // Set first student as default if any exist
          if (studentsWithWallet.length > 0) {
            setSelectedStudentId(studentsWithWallet[0].id);
          }
          setLoading(false);
          return;
        }
        
        const foundStudent = students.find(s => 
          s.walletAddress && s.walletAddress.toLowerCase() === walletAddress.toLowerCase()
        );

        if (foundStudent) {
          console.log('Found student:', foundStudent);
          setStudent(foundStudent);
          setSelectedStudentId(foundStudent.id);
        } else {
          console.log('No student found for wallet:', walletAddress);
          console.log('Available students:', students.map(s => ({ id: s.id, wallet: s.walletAddress })));
        }
      } catch (e) {
        console.error('Error loading student data:', e);
      } finally {
        setLoading(false);
      }
    };

    loadStudentData();
  }, [walletAddress]);

  // Accept selected student id passed as a prop (Dashboard passes `currentStudentId`)
  useEffect(() => {
    try {
      if (currentStudentIdProp) {
        setSelectedStudentId(currentStudentIdProp);
      }
      if (typeof isAdminProp !== 'undefined' && isAdminProp !== null) {
        setIsAdmin(Boolean(isAdminProp));
      }
    } catch (e) { /* ignore */ }
  }, [currentStudentIdProp, isAdminProp]);

  // expose normalized wallet address for child components
  const walletAddr = (walletAddress || student?.walletAddress || student?.wallet || '').toString().toLowerCase();
  const [selectedWalletForView, setSelectedWalletForView] = useState('');
  const [collectablePreviews, setCollectablePreviews] = useState([]);
  const [loadingCollectablePreviews, setLoadingCollectablePreviews] = useState(false);
  // Checkout form state
  const [showCheckoutForm, setShowCheckoutForm] = useState(false);
  const [checkoutPlan, setCheckoutPlan] = useState(null);
  // animation trigger key for badges (update when selected student changes)
  const [badgesAnimateKey, setBadgesAnimateKey] = useState(null);

  // Trigger badge intro animation when a different student is selected (one-shot)
  useEffect(() => {
    try {
      setBadgesAnimateKey(Date.now());
    } catch (e) {}
  }, [selectedStudentId]);

  // Sync route <-> activeTab for simple client routes
  useEffect(() => {
    // Helper: extract a Pinata/IPFS CID or image path from an order record
    function extractCidFromOrder(o) {
      try {
        if (!o) return null;
        const candidates = [o.pinataCid, o.cid, o.ipfsCid, o.tokenCID, o.ipfs, o.tokenURI, o.token_uri, o.image, o.metadata && o.metadata.image, o.metadata && o.metadata.cid];
        let v = candidates.find(x => x);
        if (!v && o.tokenURI && typeof o.tokenURI === 'string') v = o.tokenURI;
        if (!v) return null;
        v = String(v).trim();
        if (!v) return null;
        // handle ipfs://tokenURI or ipfs://CID/...
        if (v.startsWith('ipfs://')) {
          return v.replace(/^ipfs:\/\//, '').split('/')[0];
        }
        // if a full URL with /ipfs/<CID>
        const m = v.match(/ipfs\/([^/?#]+)/i);
        if (m && m[1]) return m[1];
        // if it's already a CID-like string
        if (/^[A-Za-z0-9_-]{40,}$/i.test(v) || /^[A-Za-z0-9]{46,}$/i.test(v)) return v;
        return null;
      } catch (e) {
        return null;
      }
    }

    // Try to resolve a usable preview image URL for an order (prefer pinata gateway)
    function resolvePreviewUrlFromOrder(o) {
      try {
        const cid = extractCidFromOrder(o);
        if (cid) return `${IPFS_GATEWAY}/ipfs/${cid}`;
        // fall back to metadata image URL if present
        const img = (o.metadata && (o.metadata.image || o.metadata.image_url)) || o.image || o.previewUrl || o.tokenImage;
        if (img && typeof img === 'string') {
          if (img.startsWith('ipfs://')) return `${IPFS_GATEWAY}/ipfs/${img.replace(/^ipfs:\/\//, '').split('/')[0]}`;
          return img;
        }
        return null;
      } catch (e) { return null; }
    }

    // Fetch order list for the current wallet and build preview thumbnails
    let mounted = true;
    const loadPreviews = async () => {
      try {
        if (activeTab !== 'collectables' || !student) {
          if (mounted) setCollectablePreviews([]);
          return;
        }

        const wallet = (selectedWalletForView || walletAddr || (student && (student.walletAddress || student.wallet || ''))).toString().toLowerCase();
        if (!wallet) {
          if (mounted) setCollectablePreviews([]);
          return;
        }

        setLoadingCollectablePreviews(true);
        const res = await apiFetch(`/api/orders/by-wallet/${encodeURIComponent(wallet)}`);
        let orders = [];
        if (res && res.ok) {
          try { orders = await res.json(); } catch (e) { orders = []; }
        } else {
          // Try a fallback endpoint
          try {
            const r2 = await apiFetch(`/api/orders?wallet=${encodeURIComponent(wallet)}`);
            if (r2 && r2.ok) orders = await r2.json();
          } catch (e) { /* ignore */ }
        }

        const previews = (Array.isArray(orders) ? orders : []).map(o => ({
          orderId: o.id || o.orderId || o._id,
          title: o.title || o.productName || o.name || (`Order ${o.id || o.orderId || ''}`),
          imageUrl: resolvePreviewUrlFromOrder(o),
          minted: !!(o.minted || o.isMinted || (o.meta && o.meta.minted)),
          raw: o
        }));

        if (mounted) setCollectablePreviews(previews);
      } catch (err) {
        console.warn('Failed to load collectable previews', err);
        if (mounted) setCollectablePreviews([]);
      } finally {
        if (mounted) setLoadingCollectablePreviews(false);
      }
    };

    loadPreviews();
    return () => { mounted = false; };
  }, [activeTab, student?.id, walletAddr, selectedWalletForView, student]);

  useEffect(() => {
    try {
      const path = window.location.pathname || '';
      if (path.endsWith('/portal/purchase-history') || path.endsWith('/portal/purchase-history/')) setActiveTab('purchases');
      else if (path.endsWith('/portal/collectables') || path.endsWith('/portal/collectables/')) setActiveTab('collectables');
    } catch (e) { /* ignore */ }
  }, []);

  useEffect(() => {
    try {
      if (activeTab === 'purchases') window.history.replaceState({}, '', '/portal/purchase-history');
      else if (activeTab === 'collectables') window.history.replaceState({}, '', '/portal/collectables');
      else window.history.replaceState({}, '', '/portal');
    } catch (e) { /* ignore */ }
  }, [activeTab]);

  // Load attendance and messages when student is selected (for both student and admin)
  useEffect(() => {
    if (!selectedStudentId) return;

    try {
      // Get students from localStorage or window.BragaBJJ
      let studentsRaw = localStorage.getItem('students');
      let students = studentsRaw ? JSON.parse(studentsRaw) : [];
      
      if (!students || students.length === 0) {
        if (window.BragaBJJ && Array.isArray(window.BragaBJJ.students)) {
          students = window.BragaBJJ.students;
        }
      }
      
      const selectedStudent = students.find(s => s.id === selectedStudentId);
      
        if (selectedStudent) {
          console.log('Loading data for student:', selectedStudent.name || selectedStudent.id);
          setStudent(selectedStudent);

          // Load attendance records - handle both object and array formats
          let studentAttendance = [];
          const attendanceRaw = localStorage.getItem('attendance');

          if (attendanceRaw) {
            const allAttendance = JSON.parse(attendanceRaw);

            if (Array.isArray(allAttendance)) {
              // Array format: filter by studentId
              studentAttendance = allAttendance.filter(a => a.studentId === selectedStudent.id).map(a => a.timestamp || a);
            } else if (typeof allAttendance === 'object') {
              // Object format: { studentId: [dates], ... }
              studentAttendance = allAttendance[selectedStudent.id] || [];
            }
          }

          // Also try window.BragaBJJ.attendance
          if (window.BragaBJJ && window.BragaBJJ.attendance) {
            const bragaAttendance = window.BragaBJJ.attendance;
            if (Array.isArray(bragaAttendance)) {
              const filtered = bragaAttendance.filter(a => a.studentId === selectedStudent.id).map(a => a.timestamp || a);
              if (filtered.length > 0) {
                studentAttendance = filtered;
              }
            }
          }

          console.log('Loaded attendance records:', studentAttendance.length);
          setAttendance(studentAttendance);

          // Best-effort: try to fetch attendance from API and merge
          (async () => {
            try {
              let serverAttendance = null;
              try {
                // Try endpoint by student id first
                const res = await apiFetch(`/api/attendance/${encodeURIComponent(selectedStudent.id)}`);
                if (res && res.ok) {
                  serverAttendance = await res.json().catch(() => null);
                }
              } catch (e) {
                // try query-style fallback
                try {
                  const res2 = await apiFetch(`/api/attendance?studentId=${encodeURIComponent(selectedStudent.id)}`);
                  if (res2 && res2.ok) serverAttendance = await res2.json().catch(() => null);
                } catch (e2) {
                  // ignore
                }
              }

              if (serverAttendance) {
                // server may return array or object; normalize to array of timestamps or records
                let serverRecords = [];
                if (Array.isArray(serverAttendance)) {
                  serverRecords = serverAttendance;
                } else if (serverAttendance.records) {
                  serverRecords = serverAttendance.records;
                } else if (serverAttendance.attendance) {
                  serverRecords = serverAttendance.attendance;
                }

                // Normalize to array of records {studentId,timestamp}
                const normalized = serverRecords.map(r => (typeof r === 'string' ? { studentId: selectedStudent.id, timestamp: r } : r));

                // Merge with local form (support array or map formats)
                try {
                  const raw = localStorage.getItem('attendance');
                  if (!raw) {
                    // write server records
                    localStorage.setItem('attendance', JSON.stringify(normalized));
                  } else {
                    const parsed = JSON.parse(raw);
                    if (Array.isArray(parsed)) {
                      // merge unique entries by timestamp+studentId
                      const map = {};
                      parsed.concat(normalized).forEach(r => { const k = `${r.studentId}::${r.timestamp}`; map[k] = r; });
                      const merged = Object.keys(map).map(k => map[k]);
                      localStorage.setItem('attendance', JSON.stringify(merged));
                    } else if (typeof parsed === 'object') {
                      const arr = parsed[selectedStudent.id] || [];
                      const combined = arr.concat(normalized.map(r => r.timestamp));
                      const unique = Array.from(new Set(combined));
                      parsed[selectedStudent.id] = unique;
                      localStorage.setItem('attendance', JSON.stringify(parsed));
                    }
                  }
                } catch (e) {
                  console.warn('Failed to merge server attendance into local storage', e);
                }

                // update state from merged source
                try {
                  const raw2 = localStorage.getItem('attendance');
                  let mergedState = [];
                  if (raw2) {
                    const p2 = JSON.parse(raw2);
                    if (Array.isArray(p2)) mergedState = p2.filter(a => a.studentId === selectedStudent.id).map(a => a.timestamp || a);
                    else if (p2[selectedStudent.id]) mergedState = p2[selectedStudent.id];
                  }
                  setAttendance(mergedState);
                } catch (e) { /* ignore */ }
              }
            } catch (err) {
              console.warn('Error fetching attendance from API', err);
            }
            })();

        // Load messages
        const messagesRaw = localStorage.getItem('studentMessages');
        const allMessages = messagesRaw ? JSON.parse(messagesRaw) : {};
        const studentMessages = allMessages[selectedStudent.id] || [];
        setMessages(studentMessages);
      } else {
        // Fallback: if we don't have the selected student in localStorage, try the admin-provided list
        const fallback = (allStudentsWithWallet || []).find(s => s.id === selectedStudentId);
        if (fallback) {
          console.log('Selected student not found in localStorage, using allStudentsWithWallet fallback:', fallback);
          setStudent(fallback);
          // clear attendance/messages so components load fresh; child components will fetch orders/nfts by wallet
          setAttendance([]);
          const messagesRaw = localStorage.getItem('studentMessages');
          const allMessages = messagesRaw ? JSON.parse(messagesRaw) : {};
          setMessages(allMessages[fallback.id] || []);
        } else {
          console.warn('Selected student not found:', selectedStudentId);
        }
      }
    } catch (e) {
      console.error('Error loading selected student data:', e);
    }
  }, [selectedStudentId, allStudentsWithWallet]);

  // Listen for student updates
  useEffect(() => {
    const handleStudentUpdate = (e) => {
      if (e.detail?.id === student?.id) {
        setStudent(prev => ({ ...prev, ...e.detail }));
      }
    };

    const handleStudentsSync = () => {
      // Reload student data
      try {
        const studentsRaw = localStorage.getItem('students');
        const students = studentsRaw ? JSON.parse(studentsRaw) : [];
        const foundStudent = students.find(s => 
          s.walletAddress && s.walletAddress.toLowerCase() === walletAddress.toLowerCase()
        );
        if (foundStudent) {
          setStudent(foundStudent);
        }
      } catch (e) {
        console.error('Error reloading student:', e);
      }
    };

    window.addEventListener('student-updated', handleStudentUpdate);
    window.addEventListener('students-synced', handleStudentsSync);

    return () => {
      window.removeEventListener('student-updated', handleStudentUpdate);
      window.removeEventListener('students-synced', handleStudentsSync);
    };
  }, [student?.id, walletAddress]);

  // Listen for ControlCenter forced pulls (cross-tab) via localStorage broadcast
  useEffect(() => {
    const onStorage = (ev) => {
      try {
        if (!ev) return;
        if (ev.key === 'braga_force_pull' || ev.key === 'bragabjj_force_pull') {
          console.log('StudentPortal: detected force-pull via storage event', ev);
          (async () => {
            try {
              const data = await fetchAggregatedData({ force: true, retries: 4, backoff: 400 });
              if (data) {
                const studs = Array.isArray(data) ? data : (data.students || []);
                if (studs && studs.length) {
                  try { localStorage.setItem('students', JSON.stringify(studs)); } catch (e) { /* ignore */ }
                  if (isAdmin) {
                    const withWallet = studs.filter(s => s.walletAddress || s.wallet);
                    setAllStudentsWithWallet(withWallet);
                    if (!student && withWallet.length) setSelectedStudentId(withWallet[0].id);
                  } else if (walletAddress) {
                    const found = studs.find(s => (s.walletAddress || s.wallet || '').toLowerCase() === walletAddress.toLowerCase());
                    if (found) { setStudent(found); setSelectedStudentId(found.id); }
                  }
                }
                if (data.attendance) try { localStorage.setItem('attendance', JSON.stringify(data.attendance)); } catch (e) { /* ignore */ }
                // dispatch global sync event
                window.dispatchEvent(new CustomEvent('students-synced', { detail: { students: Array.isArray(data) ? data : data.students } }));
              } else {
                // fallback to localStorage
                const raw = localStorage.getItem('students');
                if (raw) {
                  const studs = JSON.parse(raw);
                  if (isAdmin) setAllStudentsWithWallet(studs.filter(s => s.walletAddress || s.wallet));
                  if (!isAdmin && walletAddress) {
                    const found = studs.find(s => (s.walletAddress || s.wallet || '').toLowerCase() === walletAddress.toLowerCase());
                    if (found) { setStudent(found); setSelectedStudentId(found.id); }
                  }
                }
              }
            } catch (err) {
              console.warn('StudentPortal: failed to react to force-pull storage event', err);
            }
          })();
        }
      } catch (e) {
        console.warn('StudentPortal storage handler error', e);
      }
    };

    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, [walletAddress, isAdmin, student]);

  // Build admin wallet list from available students and env/localStorage
  useEffect(() => {
    try {
      const wallets = new Set();
      const map = {};
      // allStudentsWithWallet is populated for admin selector; use it if available
      (allStudentsWithWallet || []).forEach(s => {
        const w = (s.walletAddress || s.wallet || s.address || '').toString().toLowerCase();
        if (w) wallets.add(w);
        if (s.isAdmin) {
          map[w || s.id] = s;
          if (w) wallets.add(w);
        }
      });

      const envList = (process.env.REACT_APP_ADMIN_WALLETS || '').split(',').map(x => x.trim().toLowerCase()).filter(Boolean);
      envList.forEach(w => wallets.add(w));

      const localAdmin = (localStorage.getItem('adminWallet') || '').toLowerCase();
      if (localAdmin) wallets.add(localAdmin);

      setAdminWallets(Array.from(wallets));
      setAdminStudentsMap(map);
    } catch (e) {
      console.warn('Failed to build admin wallet list', e);
    }
  }, [allStudentsWithWallet]);

  // If admin mode is active but no students with wallets were found, try a one-time refresh
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        if (isAdmin && Array.isArray(allStudentsWithWallet) && allStudentsWithWallet.length === 0) {
          // Try fetching aggregated data once to see if any students have wallets
          try {
            const data = await loadDataFromServer();
            const studs = Array.isArray(data) ? data : (data && data.students) ? data.students : [];
            const studentsWithWallet = (studs || [])
              .map(s => ({ ...s, walletAddress: (s.walletAddress || s.wallet || s.wallet_address || s.address || '').toString().toLowerCase() }))
              .filter(s => s.walletAddress);
            if (!cancelled && studentsWithWallet.length > 0) {
              setAllStudentsWithWallet(studentsWithWallet);
              const adminMap = {};
              studentsWithWallet.forEach(s => { if (s.isAdmin) adminMap[s.walletAddress] = s; });
              setAdminStudentsMap(adminMap);
              // prefer seeded samuel-braga if present
              const seeded = studentsWithWallet.find(s => (s.id || '').toString().toLowerCase() === 'samuel-braga' || (s.id || '').toString().toLowerCase() === 'samuel_braga');
              if (seeded) {
                setSelectedStudentId(seeded.id);
                setStudent(seeded);
              } else {
                setSelectedStudentId(studentsWithWallet[0].id);
              }
            }
          } catch (e) {
            // ignore - we will keep showing admin hint if no wallets
          }
        }
      } catch (err) {
        // ignore
      }
    })();
    return () => { cancelled = true; };
  }, [isAdmin, allStudentsWithWallet]);

  // `stats` is calculated earlier above so hooks/effects can safely reference it.

  // Flowchart interaction handlers
  const handleNodeClick = (node) => {
    setSelectedNode(selectedNode?.id === node.id ? null : node);
  };

  const handleNodeMouseDown = (e, node) => {
    e.preventDefault();
    e.stopPropagation();
    setDraggedNode(node);
    setDragStart({ clientX: e.clientX, clientY: e.clientY, nodeX: node.x, nodeY: node.y });
  };

  const handleTouchStartNode = (e, node) => {
    try {
      e.preventDefault();
      e.stopPropagation();
      const t = (e.touches && e.touches[0]) || null;
      if (!t) return;
      setDraggedNode(node);
      nodeTouchRef.current = { clientX: t.clientX, clientY: t.clientY, nodeX: node.x, nodeY: node.y, startTime: Date.now() };
    } catch (err) {
      // ignore
    }
  };

  const handleTouchStart = (e) => {
    try {
      const touches = e.touches || [];
      if (touches.length === 2) {
        e.preventDefault();
        const t0 = touches[0], t1 = touches[1];
        const dx = t1.clientX - t0.clientX;
        const dy = t1.clientY - t0.clientY;
        const dist = Math.hypot(dx, dy) || 1;
        const mx = (t0.clientX + t1.clientX) / 2;
        const my = (t0.clientY + t1.clientY) / 2;
        pinchRef.current = { startDist: dist, startZoom: flowZoom, startPan: flowPan, midClient: { mx, my } };
        return;
      }

      if (touches.length === 1) {
        const t = touches[0];
        touchPanRef.current = { clientX: t.clientX, clientY: t.clientY, panStart: flowPan };
      }
    } catch (err) {
      // ignore
    }
  };

  const handleMouseMove = (e) => {
    if (draggedNode && editMode && dragStart) {
      const dx = e.clientX - dragStart.clientX;
      const dy = e.clientY - dragStart.clientY;
      const x = dragStart.nodeX + dx / flowZoom;
      const y = dragStart.nodeY + dy / flowZoom;

      setFlowchartData(prev => ({
        ...prev,
        [flowchartView]: {
          ...prev[flowchartView],
          nodes: prev[flowchartView].nodes.map(n => 
            n.id === draggedNode.id ? { ...n, x, y } : n
          )
        }
      }));
    }
  };

  const handleTouchMove = (e) => {
    try {
      const touches = e.touches || [];
      if (touches.length === 2 && pinchRef.current) {
        e.preventDefault();
        const t0 = touches[0], t1 = touches[1];
        const dx = t1.clientX - t0.clientX;
        const dy = t1.clientY - t0.clientY;
        const dist = Math.hypot(dx, dy) || 1;
        const newZoom = Math.max(0.5, Math.min(2.5, pinchRef.current.startZoom * (dist / pinchRef.current.startDist)));
        const mx = (t0.clientX + t1.clientX) / 2;
        const my = (t0.clientY + t1.clientY) / 2;
        const scaleRatio = newZoom / pinchRef.current.startZoom;
        const oldPan = pinchRef.current.startPan || { x: 0, y: 0 };
        const newPanX = oldPan.x + (1 - scaleRatio) * (mx - oldPan.x);
        const newPanY = oldPan.y + (1 - scaleRatio) * (my - oldPan.y);
        setFlowZoom(newZoom);
        setFlowPan({ x: newPanX, y: newPanY });
        return;
      }

      // node touch drag
      if (nodeTouchRef.current && touches.length === 1) {
        e.preventDefault();
        const t = touches[0];
        const dx = t.clientX - nodeTouchRef.current.clientX;
        const dy = t.clientY - nodeTouchRef.current.clientY;
        const x = nodeTouchRef.current.nodeX + dx / flowZoom;
        const y = nodeTouchRef.current.nodeY + dy / flowZoom;
        setFlowchartData(prev => ({
          ...prev,
          [flowchartView]: {
            ...prev[flowchartView],
            nodes: prev[flowchartView].nodes.map(n => n.id === draggedNode?.id ? { ...n, x, y } : n)
          }
        }));
        return;
      }

      // single-finger pan when not interacting with node
      if (touchPanRef.current && touches.length === 1) {
        e.preventDefault();
        const t = touches[0];
        const dx = t.clientX - touchPanRef.current.clientX;
        const dy = t.clientY - touchPanRef.current.clientY;
        setFlowPan({ x: touchPanRef.current.panStart.x + dx, y: touchPanRef.current.panStart.y + dy });
        return;
      }
    } catch (err) {
      // ignore
    }
  };

  const handleTouchEnd = (e) => {
    try {
      // If a node touch was in progress, detect a quick tap (short time + minimal movement)
      if (nodeTouchRef.current && draggedNode) {
        const last = (e.changedTouches && e.changedTouches[0]) || null;
        const lastX = last ? last.clientX : nodeTouchRef.current.clientX;
        const lastY = last ? last.clientY : nodeTouchRef.current.clientY;
        const dx = lastX - nodeTouchRef.current.clientX;
        const dy = lastY - nodeTouchRef.current.clientY;
        const dist = Math.hypot(dx, dy);
        const dt = Date.now() - (nodeTouchRef.current.startTime || 0);
        if (dist < 12 && dt < 350) {
          // Toggle selection to open editor on tap
          setSelectedNode(prev => (prev && prev.id === draggedNode.id) ? null : draggedNode);
        }
      }

      if (pinchRef.current) pinchRef.current = null;
      touchPanRef.current = null;
      nodeTouchRef.current = null;
      setDraggedNode(null);
    } catch (err) {}
  };

  const handleMouseUp = () => {
    setDraggedNode(null);
    setDragStart(null);
  };

  const zoomIn = () => setFlowZoom(z => Math.min(2.5, +(z + 0.25).toFixed(2)));
  const zoomOut = () => setFlowZoom(z => Math.max(0.5, +(z - 0.25).toFixed(2)));
  const resetZoom = () => { setFlowZoom(1); setFlowPan({ x: 0, y: 0 }); };
  const panBy = (dx, dy) => setFlowPan(p => ({ x: p.x + dx, y: p.y + dy }));

  const addTechniqueNode = (type) => {
    const newNode = {
      id: `node_${Date.now()}`,
      label: type === 'guard' ? 'New Guard' : type === 'pass' ? 'New Pass' : type === 'takedown' ? 'New Takedown' : 'New Submission',
      type,
      x: 400 + Math.random() * 100,
      y: 300 + Math.random() * 100,
      editable: true,
      width: 150,
      height: 50,
      fillColor: type === 'guard' ? 'rgba(59, 130, 246, 0.35)' : type === 'pass' ? 'rgba(168, 85, 247, 0.35)' : type === 'takedown' ? 'rgba(34, 197, 94, 0.35)' : 'rgba(239, 68, 68, 0.35)'
    };
    setFlowchartData(prev => ({
      ...prev,
      [flowchartView]: {
        ...prev[flowchartView],
        nodes: [...prev[flowchartView].nodes, newNode]
      }
    }));
    setSelectedNode(newNode);
  };

  // Helper: convert rgb/rgba or hex color strings to hex color for color input values
  const rgbToHex = (color) => {
    try {
      if (!color) return '#3b82f6';
      if (color.startsWith('#')) return color;
      const m = color.match(/rgba?\((\d+),\s*(\d+),\s*(\d+)/i);
      if (m) {
        const r = parseInt(m[1], 10);
        const g = parseInt(m[2], 10);
        const b = parseInt(m[3], 10);
        return '#' + [r, g, b].map(x => x.toString(16).padStart(2, '0')).join('');
      }
      return '#3b82f6';
    } catch (err) {
      return '#3b82f6';
    }
  };

  const updateNodeLabel = (nodeId, newLabel) => {
    setFlowchartData(prev => ({
      ...prev,
      [flowchartView]: {
        ...prev[flowchartView],
        nodes: prev[flowchartView].nodes.map(n => 
          n.id === nodeId ? { ...n, label: newLabel } : n
        )
      }
    }));
  };

  // Generic updater for node properties (width, height, fillColor, etc.)
  const updateNodeProps = (nodeId, props) => {
    setFlowchartData(prev => ({
      ...prev,
      [flowchartView]: {
        ...prev[flowchartView],
        nodes: prev[flowchartView].nodes.map(n => n.id === nodeId ? { ...n, ...props } : n)
      }
    }));

    // keep selectedNode in sync if it's the one being edited
    if (selectedNode && selectedNode.id === nodeId) {
      setSelectedNode(s => ({ ...s, ...props }));
    }
  };

  const deleteNode = (nodeId) => {
    setFlowchartData(prev => ({
      ...prev,
      [flowchartView]: {
        ...prev[flowchartView],
        nodes: prev[flowchartView].nodes.filter(n => n.id !== nodeId),
        edges: prev[flowchartView].edges.filter(e => e.from !== nodeId && e.to !== nodeId)
      }
    }));
    setSelectedNode(null);
  };

  const toggleEdge = (fromId, toId) => {
    setFlowchartData(prev => {
      const edgeExists = prev[flowchartView].edges.some(e => e.from === fromId && e.to === toId);
      if (edgeExists) {
        return {
          ...prev,
          [flowchartView]: {
            ...prev[flowchartView],
            edges: prev[flowchartView].edges.filter(e => !(e.from === fromId && e.to === toId))
          }
        };
      } else {
        return {
          ...prev,
          [flowchartView]: {
            ...prev[flowchartView],
            edges: [...prev[flowchartView].edges, { from: fromId, to: toId, label: 'transition' }]
          }
        };
      }
    });
  };

  const resetFlowchart = () => {
    if (window.confirm('Reset flowchart to default? This cannot be undone.')) {
      setFlowchartData(getDefaultFlowchartData());
      setSelectedNode(null);
    }
  };

  const saveFlowchart = () => {
    localStorage.setItem(`flowchart_${walletAddress}`, JSON.stringify(flowchartData));
    setEditMode(false);
    setSelectedNode(null);
  };

  const cancelEdit = () => {
    const saved = localStorage.getItem(`flowchart_${walletAddress}`);
    if (saved) {
      setFlowchartData(JSON.parse(saved));
    } else {
      setFlowchartData(getDefaultFlowchartData());
    }
    setEditMode(false);
    setSelectedNode(null);
  };

  // Send message to admin
  const sendMessage = useCallback(async () => {
    if (!newMessage.trim() || !student) return;

    setSendingMessage(true);
    try {
      const message = {
        id: `msg_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
        from: 'student',
        to: 'admin',
        studentId: student.id,
        studentName: student.name || `${student.firstName} ${student.lastName}`,
        studentEmail: student.email,
        studentPhone: student.phone,
        body: newMessage.trim(),
        timestamp: new Date().toISOString(),
        read: false
      };

      // Save to localStorage
      const messagesRaw = localStorage.getItem('studentMessages');
      const allMessages = messagesRaw ? JSON.parse(messagesRaw) : {};
      
      if (!allMessages[student.id]) {
        allMessages[student.id] = [];
      }
      
      allMessages[student.id].push(message);
      localStorage.setItem('studentMessages', JSON.stringify(allMessages));

      // Update local state
      setMessages(prev => [...prev, message]);
      setNewMessage('');

      // Broadcast to admin
      window.dispatchEvent(new CustomEvent('student-message-sent', {
        detail: { studentId: student.id, message }
      }));

      // BroadcastChannel for cross-tab sync
      try {
        const bc = new BroadcastChannel('braga-sync');
        bc.postMessage({ type: 'student-message', studentId: student.id, message });
        bc.close();
      } catch (e) {
        console.warn('BroadcastChannel not available:', e);
      }

      // ===================================================================
      // API INTEGRATIONS: Send notifications to admin via multiple channels
      // ===================================================================
      
      // Get admin notification preferences
      const adminPrefs = JSON.parse(localStorage.getItem('adminNotificationPrefs') || '{}');
      const adminEmail = localStorage.getItem('adminEmail') || process.env.REACT_APP_ADMIN_EMAIL;
      const adminPhone = localStorage.getItem('adminPhone') || process.env.REACT_APP_ADMIN_PHONE;

      // Prepare notification content
      const notificationSubject = `🥋 Student Message from ${message.studentName}`;
      // (notificationBody removed - HTML payload used for email/graph deliveries)

      const notificationHTML = `
<div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto; padding: 20px; background: linear-gradient(135deg, #7c3aed 0%, #22d3ee 100%); border-radius: 12px;">
  <div style="background: white; padding: 30px; border-radius: 8px;">
    <h2 style="color: #7c3aed; margin: 0 0 20px;">🥋 New Student Message</h2>
    <div style="background: #f8fafc; padding: 20px; border-radius: 8px; margin-bottom: 20px;">
      <p style="margin: 5px 0;"><strong>Student:</strong> ${message.studentName}</p>
      <p style="margin: 5px 0;"><strong>Email:</strong> ${message.studentEmail || 'Not provided'}</p>
      <p style="margin: 5px 0;"><strong>Phone:</strong> ${message.studentPhone || 'Not provided'}</p>
      <p style="margin: 5px 0;"><strong>Wallet:</strong> <code style="background: #e2e8f0; padding: 2px 6px; border-radius: 4px;">${walletAddress}</code></p>
    </div>
    <div style="background: #f1f5f9; padding: 20px; border-radius: 8px; border-left: 4px solid #22d3ee;">
      <h3 style="margin: 0 0 10px; color: #0f172a;">Message:</h3>
      <p style="margin: 0; color: #334155; white-space: pre-wrap;">${message.body}</p>
    </div>
    <hr style="border: none; border-top: 1px solid #e2e8f0; margin: 20px 0;">
    <p style="color: #64748b; font-size: 12px; margin: 0;">
      Sent from BragaBJJ Student Portal on ${new Date().toLocaleString()}
    </p>
  </div>
</div>
      `.trim();

      // Send via API endpoints (non-blocking)
      const apiPromises = [];

      // 1. EMAIL via SMTP (if enabled and email exists)
      if (adminPrefs.emailNotifications !== false && adminEmail) {
        apiPromises.push(
          apiFetch('/api/smtp/send', {
            method: 'POST',
            body: JSON.stringify({
              to: [adminEmail],
              subject: notificationSubject,
              html: notificationHTML
            })
          })
          .then(r => r.json())
          .then(data => console.log('✅ Email notification sent:', data))
          .catch(err => console.warn('⚠️  Email notification failed:', err))
        );
      }

      // 2. SMS via Twilio (if enabled and phone exists)
      if (adminPrefs.smsNotifications !== false && adminPhone) {
        const smsBody = `🥋 BragaBJJ: ${message.studentName} sent a message: "${message.body.substring(0, 100)}${message.body.length > 100 ? '...' : ''}"`;
        apiPromises.push(
          apiFetch('/api/twilio/send', {
            method: 'POST',
            body: JSON.stringify({
              to: [adminPhone],
              body: smsBody
            })
          })
          .then(r => r.json())
          .then(data => console.log('✅ SMS notification sent:', data))
          .catch(err => console.warn('⚠️  SMS notification failed:', err))
        );
      }

      // 3. Microsoft Graph (if enabled and access token exists)
      if (adminPrefs.graphNotifications !== false) {
        const graphToken = localStorage.getItem('msGraphAccessToken');
        if (graphToken && adminEmail) {
          apiPromises.push(
            apiFetch('/api/msgraph/send', {
              method: 'POST',
              body: JSON.stringify({
                access_token: graphToken,
                to: [adminEmail],
                subject: notificationSubject,
                html: notificationHTML
              })
            })
            .then(r => r.json())
            .then(data => console.log('✅ MS Graph notification sent:', data))
            .catch(err => console.warn('⚠️  MS Graph notification failed:', err))
          );
        }
      }

      // Execute all API calls in parallel (non-blocking)
      if (apiPromises.length > 0) {
        Promise.allSettled(apiPromises).then(results => {
          const successful = results.filter(r => r.status === 'fulfilled').length;
          const failed = results.filter(r => r.status === 'rejected').length;
          console.log(`📬 Notifications sent: ${successful} successful, ${failed} failed`);
        });
      }

    } catch (e) {
      console.error('Error sending message:', e);
      alert('Failed to send message. Please try again.');
    } finally {
      setSendingMessage(false);
    }
  }, [newMessage, student, walletAddress]);

  // Save edited profile
  const saveProfile = useCallback(async () => {
    if (!editedProfile) return;

    setSavingProfile(true);
    try {
      // Create new family member
      if (isCreating) {
        const studentsRaw = localStorage.getItem('students');
        const students = studentsRaw ? JSON.parse(studentsRaw) : [];
        const newId = `student_${Date.now()}`;
        const newStudent = {
          id: newId,
          firstName: editedProfile.firstName || 'New',
          lastName: editedProfile.lastName || 'Student',
          name: (editedProfile.firstName || 'New') + ' ' + (editedProfile.lastName || 'Student'),
          email: editedProfile.email || '',
          phone: editedProfile.phone || '',
          address: editedProfile.address || '',
          emergencyContact: editedProfile.emergencyContact || '',
          ageCategory: editedProfile.ageCategory || 'adult',
          belt: editedProfile.belt || (editedProfile.ageCategory === 'kid' ? 'white' : 'white'),
          parentId: editedProfile.parentId || null,
          joinDate: new Date().toISOString(),
          lastUpdated: new Date().toISOString()
        };

        students.push(newStudent);
        try { localStorage.setItem('students', JSON.stringify(students)); } catch (e) { /* ignore */ }

        setShowEditModal(false);
        setEditedProfile({});
        setIsCreating(false);
        setStudentsVersion(v => v + 1);

        // Broadcast new student list and the created student
        window.dispatchEvent(new CustomEvent('students-synced', { detail: { students } }));
        window.dispatchEvent(new CustomEvent('student-updated', { detail: newStudent }));

        // Best-effort API persist
        try {
          await apiFetch('/api/students', { method: 'POST', body: JSON.stringify(newStudent) });
        } catch (apiError) {
          console.warn('API create student failed (best-effort):', apiError);
        }

        alert('Family member created successfully!');
        return;
      }

      // Edit existing profile
      // Determine which student record to update: either the currently selected `student` or the one supplied in `editedProfile` (for editing family members)
      const targetId = editedProfile.id || (student && student.id);
      if (!targetId) return;

      // Load existing students
      const studentsRaw = localStorage.getItem('students');
      const students = studentsRaw ? JSON.parse(studentsRaw) : [];

      const existing = students.find(s => s.id === targetId) || {};
      const updatedStudent = { ...existing, ...editedProfile, lastUpdated: new Date().toISOString() };

      // Update localStorage list
      const updatedStudents = students.map(s => s.id === targetId ? updatedStudent : s);
      try { localStorage.setItem('students', JSON.stringify(updatedStudents)); } catch (e) { /* ignore */ }

      // If we updated the currently selected student, update state
      if (student && updatedStudent.id === student.id) {
        setStudent(updatedStudent);
      }

      setShowEditModal(false);
      setEditedProfile({});
      setStudentsVersion(v => v + 1);

      // Sync to API
      try {
        await apiFetch('/api/students/update', {
          method: 'POST',
          body: JSON.stringify(updatedStudent)
        });
      } catch (apiError) {
        console.warn('API sync failed:', apiError);
      }

      // Broadcast update
      window.dispatchEvent(new CustomEvent('student-updated', { detail: updatedStudent }));

      alert('Profile updated successfully!');
    } catch (e) {
      console.error('Failed to save profile:', e);
      alert('Failed to save profile. Please try again.');
    } finally {
      setSavingProfile(false);
    }
  }, [student, editedProfile, isCreating]);

  // Family members derived from localStorage; bump `studentsVersion` to refresh
  const familyMembers = useMemo(() => {
    try {
      // reference studentsVersion so lint knows we depend on it to force recompute
      void studentsVersion;
      if (!student || !student.id) return [];
      const raw = localStorage.getItem('students') || '[]';
      const studs = JSON.parse(raw);
      return (studs || []).filter(s => s.parentId === student.id);
    } catch (e) {
      return [];
    }
  }, [student, studentsVersion]);

  const removeFamilyMember = useCallback((memberId) => {
    if (!memberId) return;
    const ok = window.confirm('Remove this family member? This cannot be undone.');
    if (!ok) return;
    try {
      const raw = localStorage.getItem('students') || '[]';
      const studs = JSON.parse(raw);
      const updated = (studs || []).filter(s => s.id !== memberId);
      localStorage.setItem('students', JSON.stringify(updated));
      window.dispatchEvent(new CustomEvent('students-synced', { detail: { students: updated } }));
      setStudentsVersion(v => v + 1);
      alert('Family member removed.');
    } catch (e) {
      console.error('Failed to remove family member', e);
      alert('Failed to remove family member. See console for details.');
    }
  }, []);

  // Shared MetaMask admin connect handler (used by header and Access Denied view)
  const handleAdminConnect = async () => {
    if (!window?.ethereum) {
      alert('MetaMask not detected. Install MetaMask to connect as admin.');
      return;
    }

      try {
        setConnectingWallet(true);
        const accounts = await window.ethereum?.request({ method: 'eth_requestAccounts' });
        const addr = ((accounts && accounts[0]) || '').toLowerCase();
        if (!addr) {
          alert('No wallet found.');
          return;
        }

        // Try to refresh students list from aggregated API (local dev port first, then relative)
        let data = null;
        try {
          data = await loadDataFromServer();
        } catch (e) {
          // loadDataFromServer already logs; keep going
        }

        let studentsWithWallet = [];
        if (data && (Array.isArray(data.students) || Array.isArray(data))) {
          const studs = Array.isArray(data.students) ? data.students : (Array.isArray(data) ? data : []);
          studentsWithWallet = studs.map(s => ({ ...s, walletAddress: (s.walletAddress || s.wallet || s.wallet_address || s.address || '').toString().toLowerCase() })).filter(s => s.walletAddress);
          setAllStudentsWithWallet(studentsWithWallet);

          const adminMap = {};
          studentsWithWallet.forEach(s => { if (s.isAdmin) adminMap[s.walletAddress] = s; });
          setAdminStudentsMap(adminMap);
        } else {
          // fallback to localStorage-stored students
          try {
            const cached = JSON.parse(localStorage.getItem('students') || '[]');
            studentsWithWallet = cached.map(s => ({ ...s, walletAddress: (s.walletAddress || s.wallet || s.wallet_address || s.address || '').toString().toLowerCase() })).filter(s => s.walletAddress);
            if (studentsWithWallet.length) setAllStudentsWithWallet(studentsWithWallet);
            const adminMap = {};
            studentsWithWallet.forEach(s => { if (s.isAdmin) adminMap[s.walletAddress] = s; });
            setAdminStudentsMap(adminMap);
          } catch (e) { /* ignore */ }
        }

        // If the connected wallet is the privileged super-admin, ensure mapping and grant full admin access
        try {
          if (addr && addr.toLowerCase() === SUPER_ADMIN_WALLET) {
            try { enforceSuperAdminMapping(addr); } catch (e) { /* ignore */ }
            // Try to find Samuel in either server list or cached list
            let samuel = (studentsWithWallet || []).find(s => (s.id || '').toString().toLowerCase() === 'samuel-braga' || (s.id || '').toString().toLowerCase() === 'samuel_braga');
            if (!samuel) {
              try {
                const cached = JSON.parse(localStorage.getItem('students') || '[]');
                samuel = (cached || []).find(s => (s.id || '').toString().toLowerCase() === 'samuel-braga' || (s.id || '').toString().toLowerCase() === 'samuel_braga');
              } catch (e) { /* ignore */ }
            }
            setIsAdmin(true);
            if (samuel) {
              setSelectedStudentId(samuel.id);
              setStudent({ ...samuel, walletAddress: SUPER_ADMIN_WALLET });
              window.dispatchEvent(new CustomEvent('student-updated', { detail: { ...samuel, walletAddress: SUPER_ADMIN_WALLET } }));
            }
            localStorage.setItem('adminConnectedWallet', SUPER_ADMIN_WALLET);
            alert('Admin access granted (super admin).');
            setConnectingWallet(false);
            return;
          }
        } catch (e) { console.warn('super-admin check failed', e); }

        // Build merged admin wallets list from env, localStorage and students
        const envList = (process.env.REACT_APP_ADMIN_WALLETS || '').split(',').map(w => w.trim().toLowerCase()).filter(Boolean);
        const localList = (localStorage.getItem('adminWallet') || '').split(',').map(w => w.trim().toLowerCase()).filter(Boolean);
        const fromStudents = studentsWithWallet.map(s => (s.walletAddress || '').toLowerCase()).filter(Boolean);
        const merged = Array.from(new Set([...envList, ...localList, ...fromStudents]));
        setAdminWallets(merged);

        const isAuth = merged.includes(addr) || (!!adminStudentsMap && !!adminStudentsMap[addr]);
        const envAuth = envList.includes(addr);

        if (isAuth || envAuth) {
          setIsAdmin(true);

          // prefer mapped admin student, otherwise find seeded samuel-braga (case-insensitive)
          const adminFromMap = (adminStudentsMap && adminStudentsMap[addr]) || null;
          const samuel = (studentsWithWallet || []).find(s => (s.id || '').toString().toLowerCase() === 'samuel-braga' || (s.id || '').toString().toLowerCase() === 'samuel_braga');
          const chosen = adminFromMap || samuel || null;
          if (chosen) {
            setSelectedStudentId(chosen.id);
            setStudent({ ...chosen, walletAddress: (chosen.walletAddress || '').toLowerCase() });
          }

          localStorage.setItem('adminConnectedWallet', addr);
          alert('Admin access granted.');
        } else {
          alert('Connected wallet is not authorized as admin.');
        }
    } catch (e) {
      console.error('MetaMask connect failed', e);
      alert('Failed to connect MetaMask');
    } finally {
      setConnectingWallet(false);
    }
  };

  // Calculate payment amount based on currency
  const getPaymentAmount = useCallback(() => {
    // Support plans: weekly ($39), biweekly ($78), monthly ($139), threeMonth ($339), custom (user-specified)
    let usdAmount = 39;
    if (selectedPlan === 'weekly') usdAmount = 39;
    else if (selectedPlan === 'biweekly') usdAmount = 78;
    else if (selectedPlan === 'monthly') usdAmount = 139;
    else if (selectedPlan === 'threeMonth') usdAmount = 339;
    else if (selectedPlan === 'custom') usdAmount = Number(customAmount) || 0;

    // Debug
    console.log('getPaymentAmount - Currency:', paymentCurrency, 'ETH Price:', ethPrice, 'USD Amount:', usdAmount);

    const price = (ethPrice && ethPrice > 0) ? ethPrice : 2500; // fallback price
    const ethAmount = usdAmount / price;
    const ethFormatted = ethAmount.toFixed(6);
    const wei = Math.floor(ethAmount * 1e18).toString();

    return {
      usd: usdAmount,
      eth: ethAmount,
      ethFormatted,
      wei
    };
  }, [selectedPlan, paymentCurrency, ethPrice, customAmount]);

  // Process MetaMask payment
  const processPayment = useCallback(async () => {
    if (!window?.ethereum) {
      alert('MetaMask not detected. Please install MetaMask to make payments.');
      return;
    }

    setProcessingPayment(true);
    try {
      // Request accounts
      const accounts = await window.ethereum?.request({ method: 'eth_requestAccounts' });
      const fromAddress = accounts[0];

      // Get payment amount
      const paymentAmount = getPaymentAmount();

      // Gym wallet address (from localStorage or default)
      const gymWallet = localStorage.getItem('gymWalletAddress') || 
                        localStorage.getItem('adminWallet') || 
                        '0x742d35Cc6634C0532925a3b844Bc9e7595f0bEb';

      // MetaMask expects hex string amounts (wei) prefixed with 0x
      const numericWei = typeof window !== 'undefined' && window.BigInt ? window.BigInt(paymentAmount.wei) : null;
      const valueHex = numericWei ? '0x' + numericWei.toString(16) : '0x0';
      const transactionParameters = {
        from: fromAddress,
        to: gymWallet,
        value: valueHex,
        gas: '0x5208', // 21000 in hex
      };

      // Send transaction
      const txHash = await window.ethereum?.request({
        method: 'eth_sendTransaction',
        params: [transactionParameters],
      });

      // Record payment
      const payment = {
        id: `pay_${Date.now()}`,
        studentId: student.id,
        amountUSD: paymentAmount.usd,
        amountETH: paymentAmount.ethFormatted,
        currency: paymentCurrency,
        plan: selectedPlan,
        txHash,
        timestamp: new Date().toISOString(),
        status: 'pending',
        from: fromAddress,
        to: gymWallet,
        ethPrice: ethPrice || 'N/A'
      };

      // Save to payment history
      const stored = localStorage.getItem('studentPayments');
      const allPayments = stored ? JSON.parse(stored) : {};
      if (!allPayments[student.id]) {
        allPayments[student.id] = [];
      }
      allPayments[student.id].push(payment);
      localStorage.setItem('studentPayments', JSON.stringify(allPayments));
      setPaymentHistory(allPayments[student.id]);

      // Update student's last payment date
      const updatedStudent = { ...student, lastPaymentDate: new Date().toISOString() };
      const studentsRaw = localStorage.getItem('students');
      const students = studentsRaw ? JSON.parse(studentsRaw) : [];
      const updatedStudents = students.map(s => s.id === student.id ? updatedStudent : s);
      localStorage.setItem('students', JSON.stringify(updatedStudents));
      setStudent(updatedStudent);

      // Send to API
      try {
        await apiFetch('/api/payments/record', {
          method: 'POST',
          body: JSON.stringify(payment)
        });
      } catch (apiError) {
        console.warn('API payment record failed:', apiError);
      }

      setShowPaymentModal(false);
      
      alert(`Payment submitted successfully!\n\nAmount: $${paymentAmount.usd} USD (${paymentAmount.ethFormatted} ETH)\nTransaction Hash: ${txHash}\n\nView on Etherscan: https://etherscan.io/tx/${txHash}`);

    } catch (e) {
      console.error('Payment failed:', e);
      if (e.code === 4001) {
        alert('Payment cancelled by user.');
      } else {
        alert('Payment failed: ' + (e.message || 'Unknown error'));
      }
    } finally {
      setProcessingPayment(false);
    }
  }, [student, selectedPlan, paymentCurrency, ethPrice, getPaymentAmount]);

  // Helper for mobile one-tap payments: confirm wallet then process payment
  const doMobilePay = useCallback(async (planKey) => {
    try {
      if (planKey) setSelectedPlan(planKey);
      // If custom and a preset is selected, ensure customAmount reflects it
      if (planKey === 'custom' && customPreset && customPreset !== 'other') {
        setCustomAmount(Number(customPreset));
      }
      // Ensure wallet is connected and matched
      const addr = await requestAndOpen();
      if (!addr) {
        // user cancelled or connection failed
        return;
      }
      // Proceed to payment flow
      await processPayment();
    } catch (err) {
      console.warn('doMobilePay failed', err);
      alert('Payment failed: ' + (err?.message || err));
    }
  }, [requestAndOpen, processPayment, customPreset]);

  // Calculate amounts for a given plan key (synchronous helper for modal preview)
  const calculateAmountForPlan = useCallback((planKey) => {
    let usdAmount = 39;
    if (planKey === 'weekly') usdAmount = 39;
    else if (planKey === 'biweekly') usdAmount = 78;
    else if (planKey === 'monthly') usdAmount = 139;
    else if (planKey === 'threeMonth') usdAmount = 339;
    else if (planKey === 'custom') usdAmount = Number(customAmount) || 0;

    const price = (ethPrice && ethPrice > 0) ? ethPrice : 2500;
    const ethAmount = usdAmount / price;
    const ethFormatted = ethAmount.toFixed(6);

    return { usd: usdAmount, eth: ethAmount, ethFormatted };
  }, [ethPrice, customAmount]);

  const confirmMobilePay = useCallback(async () => {
    try {
      setShowMobileConfirm(false);
      // ensure selected plan set
      setSelectedPlan(mobileConfirmPlan);
      if (mobileConfirmPlan === 'custom' && customPreset && customPreset !== 'other') {
        setCustomAmount(Number(customPreset));
      }
      // proceed with existing mobile flow
      await doMobilePay(mobileConfirmPlan);
    } catch (e) {
      console.warn('confirmMobilePay failed', e);
    }
  }, [mobileConfirmPlan, customPreset, doMobilePay]);

  // Belt progression data
  const beltProgression = useMemo(() => {
    const belts = ['white', 'blue', 'purple', 'brown', 'black'];
    const currentBelt = student?.belt || 'white';
    const currentIndex = belts.indexOf(currentBelt.toLowerCase());
    
    return belts.map((belt, idx) => ({
      belt,
      completed: idx < currentIndex,
      current: idx === currentIndex,
      future: idx > currentIndex
    }));
  }, [student?.belt]);

  if (loading) {
    return (
      <div className="student-portal-overlay">
        <div className="student-portal-container">
          <div className="portal-loading">
            <div className="loading-spinner" />
            <p>Loading your profile...</p>
          </div>
        </div>
      </div>
    );
  }
  
  

  // Mobile-optimized view: simplified layout for small screens and mobile browsers
  if (isMobile) {
    if (!student) {
      return (
        <div className="student-portal-overlay" onClick={handleClose}>
          <div className={`student-portal-container mobile-mode`} onClick={(e) => e.stopPropagation()}>
            <WalletFloating />
            <div className="portal-header mobile-compact-header">
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div style={{ fontWeight: 800 }}>Student Portal</div>
                <button className="portal-close-btn" onClick={handleClose} aria-label="Close">×</button>
              </div>
              <p className="portal-subtitle">No student profile loaded.</p>
            </div>
            <div className="portal-content">
              <div style={{ padding: 16 }}>
                <p className="empty-state">No student selected. Please connect a wallet or choose a student.</p>
                <div style={{ marginTop: 12 }}>
                  <button className="btn-primary-portal" onClick={handleClose}>Close</button>
                  <button className="btn-sm" style={{ marginLeft: 8 }} onClick={async (e) => { e.stopPropagation(); try { await requestAndOpen(); } catch (err) { console.warn(err); } }}>Connect Wallet</button>
                </div>
              </div>
            </div>
          </div>
        </div>
      );
    }
    return (
      <div className="student-portal-overlay" onClick={handleClose}>
        <div className={`student-portal-container mobile-mode`} onClick={(e) => e.stopPropagation()}>
          {/* Floating wallet status for mobile */}
          <WalletFloating />
          <div className="portal-header mobile-compact-header">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12 }}>
              <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
                <div className="student-avatar" style={{ width: 52, height: 52, fontSize: 20 }}>
                  {student && (student.name ? student.name.charAt(0) : (student.firstName ? student.firstName.charAt(0) : 'S'))}
                </div>
                <div>
                  <div style={{ color: 'var(--portal-text)', fontWeight: 800, fontSize: 16 }}>{student?.name || (((student?.firstName || '') + ' ' + (student?.lastName || '')).trim()) || 'Student'}</div>
                  <div style={{ color: 'var(--portal-muted)', fontSize: 12 }}>{student?.belt || 'White'} • {student?.membershipType || 'Member'}</div>
                </div>
              </div>
              <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                <button className="portal-close-btn" onClick={handleClose} aria-label="Close">×</button>
              </div>
            </div>

            <div style={{ marginTop: 12, display: 'flex', gap: 8 }} className="mobile-header-actions">
              <button
                className={`btn-sm wallet-link-button ${student?.walletAddress ? 'connected' : 'disconnected'}`}
                onClick={async (e) => { e.stopPropagation(); await requestAndOpen(); }}
                disabled={connectingWallet}
              >
                {connectingWallet ? 'Connecting…' : (student?.walletAddress ? 'Confirm Wallet' : 'Connect Wallet')}
              </button>
              <button className="btn-sm" onClick={() => openStudentPortal(student?.id)}>
                Open Portal
              </button>
              <button className="btn-sm" onClick={handleClose}>
                Main Menu
              </button>
            </div>
          </div>

          <div className="portal-content mobile-compact-content">
            <div className="portal-stats" style={{ padding: '6px 12px' }}>
              <div className="stat-card" style={{ padding: '8px 6px' }}>
                <div className="stat-icon" style={{ fontSize: 20, marginBottom: 3 }}>🥋</div>
                <div className="stat-value" style={{ fontSize: 18, fontWeight: 700, marginBottom: 2 }}>{stats.totalClasses}</div>
                <div className="stat-label" style={{ fontSize: 9 }}>Total</div>
              </div>
              <div className="stat-card" style={{ padding: '8px 6px' }}>
                <div className="stat-icon" style={{ fontSize: 20, marginBottom: 3 }}>📅</div>
                <div className="stat-value" style={{ fontSize: 18, fontWeight: 700, marginBottom: 2 }}>{stats.thisMonth}</div>
                <div className="stat-label" style={{ fontSize: 9 }}>Month</div>
              </div>
              <div className="stat-card" style={{ padding: '8px 6px' }}>
                <div className="stat-icon" style={{ fontSize: 20, marginBottom: 3 }}>🔥</div>
                <div className="stat-value" style={{ fontSize: 18, fontWeight: 700, marginBottom: 2 }}>{stats.streak}</div>
                <div className="stat-label" style={{ fontSize: 9 }}>Streak</div>
              </div>
            </div>

          {/* Fixed crown button bottom-right for Samuel admin */}
          {((isAdmin) || (student?.id === 'samuel-braga') || (walletAddr === SUPER_ADMIN_WALLET)) && (
            <button
              title="Admin: Browse wallet-linked students"
              onClick={() => setShowWalletList(true)}
              style={{
                position: 'fixed',
                right: 16,
                bottom: 16,
                width: 56,
                height: 56,
                borderRadius: 28,
                background: 'linear-gradient(135deg,#7c3aed,#4f46e5)',
                color: '#fff',
                boxShadow: '0 8px 24px rgba(79,70,229,0.28)',
                border: 'none',
                zIndex: 99999,
                fontSize: 22
              }}
            >👑</button>
          )}

            <div style={{ marginTop: 12 }}>
              
              <div style={{ display: 'flex', gap: 6, marginBottom: 12, flexWrap: 'wrap' }}>
                <button className="btn-sm" style={{ flex: '1 1 auto', minWidth: 90 }} onClick={() => setActiveTab('profile')}>Profile</button>
                <button className="btn-sm" style={{ flex: '1 1 auto', minWidth: 90 }} onClick={() => setActiveTab('badges')}>Badges</button>
                <button className="btn-sm" style={{ flex: '1 1 auto', minWidth: 90 }} onClick={() => setActiveTab('attendance')}>Attendance</button>
                <button className="btn-sm" style={{ flex: '1 1 auto', minWidth: 90 }} onClick={() => setActiveTab('payments')}>Payments</button>
                <button className="btn-sm" style={{ flex: '1 1 auto', minWidth: 110 }} onClick={() => setActiveTab('occurring')}>Occurring Payments</button>
                <button className="btn-sm" style={{ flex: '1 1 auto', minWidth: 90 }} onClick={() => setActiveTab('store')}>Store</button>
                <button className="btn-sm" style={{ flex: '1 1 auto', minWidth: 90 }} onClick={() => setActiveTab('belt')}>Belt</button>
                <button className="btn-sm" style={{ flex: '1 1 auto', minWidth: 90 }} onClick={() => setActiveTab('awards')}>Awards</button>
                <button className="btn-sm" style={{ flex: '1 1 auto', minWidth: 90 }} onClick={() => setActiveTab('flowchart')}>Flowchart</button>
                <button className="btn-sm" style={{ flex: '1 1 auto', minWidth: 90 }} onClick={() => setActiveTab('purchases')}>Purchase History</button>
                <button className="btn-sm" style={{ flex: '1 1 auto', minWidth: 90 }} onClick={() => setActiveTab('collectables')}>Collectables</button>
                <button className="btn-sm" style={{ flex: '1 1 auto', minWidth: 90 }} onClick={() => setActiveTab('messages')}>Messages</button>
              </div>

              <div>
                {activeTab === 'badges' && (
                  <div>
                    { (typeof StudentBadgesTab === 'function' || (typeof StudentBadgesTab === 'object' && StudentBadgesTab !== null)) ? (
                      <StudentBadgesTab badges={student?.badges || []} student={student} attendance={attendance} />
                    ) : (
                      <div className="error">Something went wrong loading the Badges view. Check console for details.</div>
                    )}
                    { (typeof StudentBadgesTab === 'undefined' || (typeof StudentBadgesTab !== 'function' && typeof StudentBadgesTab !== 'object')) && console.error('StudentBadgesTab is not a valid React component:', StudentBadgesTab) }
                  </div>
                )}
                {activeTab === 'profile' && (
                  <div>
                    <div className="profile-field">
                      <label>Full Name</label>
                      <div className="field-value">{student.name || `${student.firstName || ''} ${student.lastName || ''}`.trim() || '—'}</div>
                    </div>
                    <div className="profile-field">
                      <label>Email</label>
                      <div className="field-value">{student.email || '—'}</div>
                    </div>
                    <div className="profile-field">
                      <label>Phone</label>
                      <div className="field-value">{student.phone || '—'}</div>
                    </div>
                    <div className="profile-field">
                      <label>Belt Rank</label>
                      <div className="field-value belt-badge">{student.belt || 'White'}</div>
                    </div>
                    <div className="profile-field">
                      <label>Member Since</label>
                      <div className="field-value">{student.joinDate ? new Date(student.joinDate).toLocaleDateString() : '—'}</div>
                    </div>
                    <div className="profile-field">
                      <label>Membership Type</label>
                      <div className="field-value">{student.membershipType || '—'}</div>
                    </div>
                    <div className="profile-field full-width">
                      <label>Address</label>
                      <div className="field-value">{student.address || '—'}</div>
                    </div>
                    <div className="profile-field full-width">
                      <label>Emergency Contact</label>
                      <div className="field-value">{student.emergencyContact || '—'}</div>
                    </div>

                    <div style={{ marginTop: 12, display: 'flex', gap: 8 }}>
                      <button className="btn-edit-profile" onClick={() => { setEditedProfile({ ...student }); setIsCreating(false); setShowEditModal(true); }}>Edit Profile</button>
                      <button className="btn-sm" onClick={() => { setEditedProfile({ parentId: student?.id || null, ageCategory: 'adult', belt: student?.belt || 'white' }); setIsCreating(true); setShowEditModal(true); }} aria-label="Add family member">＋ Add Family</button>
                    </div>

                    {/* Family members list */}
                    {familyMembers && familyMembers.length > 0 && (
                      <div style={{ marginTop: 14 }}>
                        <div style={{ fontWeight: 700, marginBottom: 8 }}>Family Members</div>
                        <div className="family-list">
                          {familyMembers.map(f => (
                            <div key={f.id} className="family-item" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 0', borderBottom: '1px solid rgba(0,0,0,0.06)' }}>
                              <div>
                                <div style={{ fontWeight: 700 }}>{f.name || `${f.firstName || ''} ${f.lastName || ''}`.trim() || f.id}</div>
                                <div style={{ fontSize: 13, color: 'var(--portal-muted)' }}>{(f.ageCategory || 'adult').toUpperCase()} • {f.belt || 'white'}</div>
                              </div>
                              <div style={{ display: 'flex', gap: 8 }}>
                                <button className="btn-sm" onClick={() => { setEditedProfile({ ...f }); setIsCreating(false); setShowEditModal(true); }}>Edit</button>
                                <button className="btn-sm btn-danger" onClick={() => removeFamilyMember(f.id)}>Remove</button>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {activeTab === 'attendance' && (
                  <div>
                    <h3>Recent Attendance</h3>
                    {renderAttendanceProgress()}
                    <div className="attendance-list">
                      {attendance.slice().reverse().slice(0,8).map((ts, idx) => (
                        <div key={idx} className="attendance-item">
                          <div className="attendance-icon">✓</div>
                          <div className="attendance-details">
                            <div className="attendance-date">{new Date(ts).toLocaleDateString()}</div>
                            <div className="attendance-time">{new Date(ts).toLocaleTimeString()}</div>
                          </div>
                        </div>
                      ))}
                      {attendance.length === 0 && (<p className="empty-state">No attendance yet.</p>)}
                    </div>

                    <div style={{ marginTop: 10 }}>
                      <button className="btn-primary-portal" onClick={handleCheckIn}>Check In</button>
                    </div>
                  </div>
                )}

                {activeTab === 'payments' && (
                  <div>
                    <h3>Payments</h3>

                    <div style={{ marginTop: 8 }}>
                      <div style={{ display: 'flex', gap: 8, alignItems: 'center', justifyContent: 'space-between' }}>
                        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                          <div style={{ fontWeight: 800 }}>Status</div>
                          <div className={`payment-badge payment-${stats.paymentStatus}`} style={{ marginLeft: 6 }}>{stats.paymentStatus === 'paid' ? '✓ Paid' : stats.paymentStatus === 'due' ? '⚠ Due' : '❌ Overdue'}</div>
                        </div>
                        <div style={{ fontSize: 13, color: 'var(--portal-muted)' }}>{student?.lastPaymentDate ? `Last: ${new Date(student.lastPaymentDate).toLocaleDateString()}` : 'No payments recorded'}</div>
                      </div>

                      {/* Mobile-friendly subtabs */}
                      <div style={{ marginTop: 12 }}>
                        <div style={{ display: 'flex', gap: 8, overflowX: 'auto', paddingBottom: 6 }}>
                          {['weekly','biweekly','monthly','threeMonth','custom'].map(p => (
                            <button
                              key={p}
                              onClick={() => setSelectedPlan(p)}
                              className={`subtab-btn mobile-subtab-btn ${selectedPlan === p ? 'active' : ''}`}
                            >
                              {p === 'weekly' ? 'Weekly $39' : p === 'biweekly' ? 'Bi-Weekly $78' : p === 'monthly' ? 'Monthly $139' : p === 'threeMonth' ? '3mo $339' : 'Custom'}
                            </button>
                          ))}
                        </div>

                        {/* ETH price row */}
                        <div className="mobile-eth-row">
                          <div style={{ fontSize: 13 }}>
                            {loadingEthPrice ? 'Loading ETH price…' : ethPrice ? `1 ETH ≈ $${Number(ethPrice).toLocaleString()}` : 'ETH price unavailable'}
                          </div>
                          <div>
                            <button className="btn-sm" onClick={async () => { try { await requestAndOpen(); alert('Wallet confirmed'); } catch (e) { console.warn(e); } }}>
                              {student?.walletAddress ? 'Confirm Wallet' : 'Connect Wallet'}
                            </button>
                          </div>
                        </div>

                        {/* Plan cards - horizontal scroll */}
                        <div className="mobile-plan-cards">
                          {[{
                            key: 'weekly', name: 'Weekly', usd: 39, icon: '📅', desc: 'per week'
                          },{
                            key: 'biweekly', name: 'Bi-Weekly', usd: 78, icon: '💎', desc: 'per 2 weeks'
                          },{
                            key: 'monthly', name: 'Monthly', usd: 139, icon: '🗓️', desc: 'per month'
                          },{
                            key: 'threeMonth', name: '3 Months', usd: 339, icon: '📆', desc: '3-month bundle'
                          }].map(plan => (
                            <div key={plan.key} onClick={() => setSelectedPlan(plan.key)} className={`mobile-plan-card ${selectedPlan === plan.key ? 'selected' : ''}`}>
                              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <div style={{ fontSize: 20 }}>{plan.icon}</div>
                                <div style={{ textAlign: 'right' }}>
                                  <div style={{ fontWeight: 800 }}>{plan.name}</div>
                                  <div style={{ fontSize: 12, color: 'var(--portal-muted)' }}>{plan.desc}</div>
                                </div>
                              </div>
                              <div style={{ marginTop: 10, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <div style={{ fontSize: 18, fontWeight: 900 }}>{paymentCurrency === 'USD' ? `$${plan.usd}` : (ethPrice ? `${(plan.usd / ethPrice).toFixed(6)} ETH` : '...')}</div>
                                <button className="btn-sm" onClick={() => { setSelectedPlan(plan.key); setMobileConfirmPlan(plan.key); setShowMobileConfirm(true); }}>Pay</button>
                              </div>
                            </div>
                          ))}
                          {/* Custom card */}
                          <div key="custom" onClick={() => setSelectedPlan('custom')} className={`mobile-custom-card ${selectedPlan === 'custom' ? 'selected' : ''}`}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                              <div style={{ fontSize: 20 }}>🎯</div>
                              <div style={{ textAlign: 'right' }}>
                                <div style={{ fontWeight: 800 }}>Custom</div>
                                <div style={{ fontSize: 12, color: 'var(--portal-muted)' }}>One-time amount</div>
                              </div>
                            </div>
                            <div style={{ marginTop: 10 }}>
                              <select value={customPreset} onChange={(e) => { const v = e.target.value; setCustomPreset(v); if (v !== 'other') setCustomAmount(Number(v)); }} style={{ width: '100%', padding: 8, borderRadius: 8 }}>
                                <option value="other">Enter custom amount…</option>
                                <option value="25">$25</option>
                                <option value="50">$50</option>
                                <option value="75">$75</option>
                                <option value="100">$100</option>
                                <option value="150">$150</option>
                                <option value="200">$200</option>
                              </select>
                              {customPreset === 'other' && (
                                <input type="number" min="1" value={customAmount} onChange={(e) => setCustomAmount(Number(e.target.value || 0))} style={{ width: '100%', padding: 8, borderRadius: 8, marginTop: 8 }} />
                              )}
                              <div style={{ marginTop: 10, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <div style={{ fontSize: 18, fontWeight: 800 }}>{paymentCurrency === 'USD' ? `$${customAmount}` : (ethPrice ? `${(customAmount / ethPrice).toFixed(6)} ETH` : '...')}</div>
                                <button className="btn-sm" onClick={() => { setSelectedPlan('custom'); setMobileConfirmPlan('custom'); setShowMobileConfirm(true); }}>Pay</button>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Sticky action bar for mobile (moved to CSS) */}
                      <div className="mobile-sticky-paybar">
                        <button style={{ flex: '0 0 120px' }} className="btn-sm" onClick={async () => { try { await requestAndOpen(); alert('Wallet confirmed'); } catch (e) { console.warn(e); } }}>
                          {student?.walletAddress ? 'Confirm' : 'Connect'}
                        </button>
                        <button className="btn-primary-portal" style={{ flex: 1, padding: '14px 16px', borderRadius: 12 }} onClick={async () => { setMobileConfirmPlan(selectedPlan); setShowMobileConfirm(true); }}>
                          {(() => {
                            const amt = getPaymentAmount();
                            return `Pay ${paymentCurrency === 'USD' ? `$${amt.usd}` : `${amt.ethFormatted} ETH`}`;
                          })()}
                        </button>
                      </div>

                      {/* Small spacer so content isn't hidden under fixed bar */}
                      <div style={{ height: 74 }} />
                      {/* Mobile Pay Confirmation Sheet */}
                      {showMobileConfirm && (
                        <div className="modal-overlay" onClick={() => setShowMobileConfirm(false)}>
                          <div className="modal-content payment-modal mobile-pay-confirm" onClick={(e) => e.stopPropagation()}>
                              <div className="confirm-header">
                                <div style={{ fontWeight: 800 }}>{mobileConfirmPlan === 'weekly' ? 'Weekly' : mobileConfirmPlan === 'biweekly' ? 'Bi-Weekly' : mobileConfirmPlan === 'monthly' ? 'Monthly' : mobileConfirmPlan === 'threeMonth' ? '3-Month Bundle' : 'Custom Payment'}</div>
                                <button className="modal-close" onClick={() => setShowMobileConfirm(false)}>×</button>
                              </div>
                              <div className="confirm-subtitle">Review and confirm your payment. You'll be prompted by MetaMask to approve the transaction.</div>

                              <div className="wallet-row">
                                <div>
                                  <div style={{ fontSize: 12, color: 'var(--portal-muted)' }}>Paying from</div>
                                  <div className="wallet-badge">{student?.walletAddress ? `${student.walletAddress.slice(0,6)}…${student.walletAddress.slice(-4)}` : 'No wallet linked'}</div>
                                </div>
                                <div>
                                  <button className="btn-sm" onClick={async (e) => { e.stopPropagation(); try { await requestAndOpen(); alert('Wallet confirmed'); } catch (err) { console.warn(err); } }}>
                                    {student?.walletAddress ? 'Confirm Wallet' : 'Connect Wallet'}
                                  </button>
                                </div>
                              </div>

                              <div className="confirm-amount">
                                {(() => {
                                  const amt = calculateAmountForPlan(mobileConfirmPlan);
                                  return (
                                    <div>
                                      <div className="amount-value">{paymentCurrency === 'USD' ? `$${amt.usd.toFixed(2)}` : `${amt.ethFormatted} ETH`}</div>
                                      <div className="amount-sub">≈ ${amt.usd.toFixed(2)} USD • {amt.ethFormatted} ETH</div>
                                    </div>
                                  );
                                })()}
                              </div>

                              <div className="confirm-actions">
                                <button className="btn-cancel" style={{ flex: 1 }} onClick={() => setShowMobileConfirm(false)}>Cancel</button>
                                <button className={`btn-primary-portal btn-spinner ${processingPayment ? 'processing' : ''}`} style={{ flex: 1 }} onClick={confirmMobilePay} disabled={processingPayment}>
                                  {processingPayment ? 'Processing…' : 'Confirm & Pay'}
                                </button>
                              </div>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {activeTab === 'occurring' && (
                  <div>
                    {!showCheckoutForm ? (
                      <OccurringPaymentsTab onOpenCheckout={(planId) => { setCheckoutPlan(planId); setShowCheckoutForm(true); }} />
                    ) : (
                      <div>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                          <h3 style={{ margin: 0 }}>Secure Checkout</h3>
                          <button className="btn-sm" onClick={() => { setShowCheckoutForm(false); setCheckoutPlan(null); }}>Back</button>
                        </div>
                        <CheckoutForm returnUrlBase={window.location.origin} initialPlanId={checkoutPlan} planDefinitions={RECURRING_PLANS} />
                      </div>
                    )}
                  </div>
                )}

                {activeTab === 'belt' && (
                  <div>
                    <h3 style={{ marginTop: 0 }}>Belt Progress</h3>
                    <div style={{ display: 'flex', gap: 8, overflowX: 'auto', paddingBottom: 8 }}>
                      {beltProgression.map(({ belt, completed, current }) => (
                        <div key={belt} style={{ minWidth: 100, padding: 8, borderRadius: 8, background: current ? 'linear-gradient(90deg,#7c3aed,#22d3ee)' : '#111827', color: current ? 'white' : '#cbd5e1', textAlign: 'center' }}>
                          <div style={{ fontWeight: 700 }}>{belt.charAt(0).toUpperCase() + belt.slice(1)}</div>
                          <div style={{ fontSize: 12, marginTop: 6 }}>{completed ? 'Completed' : current ? 'Current' : 'Upcoming'}</div>
                        </div>
                      ))}
                    </div>
                    <div style={{ marginTop: 12 }}>
                      <p style={{ margin: 0 }}>Total classes: <strong>{stats.totalClasses}</strong></p>
                      <p style={{ margin: 0 }}>Current streak: <strong>{stats.streak}</strong></p>
                    </div>
                  </div>
                )}

                {activeTab === 'awards' && (
                  <div>
                    <h3 style={{ marginTop: 0 }}>Awards & Achievements</h3>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 8 }}>
                      {stats.totalClasses >= 10 && (
                        <div className="award-card small">
                          <div className="award-icon">🥋</div>
                          <div className="award-title">10 Classes</div>
                        </div>
                      )}
                      {stats.totalClasses >= 50 && (
                        <div className="award-card small">
                          <div className="award-icon">🏆</div>
                          <div className="award-title">50 Classes</div>
                        </div>
                      )}
                      {stats.totalClasses >= 100 && (
                        <div className="award-card small">
                          <div className="award-icon">💎</div>
                          <div className="award-title">100 Classes</div>
                        </div>
                      )}
                      {stats.streak >= 7 && (
                        <div className="award-card small">
                          <div className="award-icon">🔥</div>
                          <div className="award-title">7-Day Streak</div>
                        </div>
                      )}
                      {stats.paymentStatus === 'paid' && (
                        <div className="award-card small">
                          <div className="award-icon">💳</div>
                          <div className="award-title">Good Standing</div>
                        </div>
                      )}
                    </div>
                    {stats.totalClasses < 10 && (
                      <p className="empty-state">Keep training to unlock more achievements!</p>
                    )}
                    { /* trophies + progression controls (mobile) */ }
                    {renderTrophies()}
                    {renderProgressControls()}

                    {/* Award / NFT mint area */}
                    {stats.totalClasses >= ATTENDANCE_TARGET && (
                      <div style={{ marginTop: 12 }} className="award-mint-area">
                        <h4>🎖️ Belt Achievement</h4>
                        <p>Congratulations — you've reached the {ATTENDANCE_TARGET}-class milestone! Claim your commemorative belt trophy NFT.</p>
                        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                          <button className="btn-primary-portal" onClick={() => setShowAwardModal(true)}>Claim Trophy NFT</button>
                          <button className="btn-sm" onClick={() => { triggerConfetti(40); }}>Celebrate</button>
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {activeTab === 'flowchart' && (
                  <div>
                    <h3 style={{ marginTop: 0 }}>Flowchart (compact)</h3>
                    <div style={{ display: 'flex', gap: 8, overflowX: 'auto', paddingBottom: 8 }}>
                      {(flowchartData[flowchartView]?.nodes || []).map(n => (
                        <div key={n.id} style={{ minWidth: 140, padding: 8, borderRadius: 8, background: '#0f172a', color: '#e6edf3' }}>
                          <div style={{ fontWeight: 700 }}>{n.label}</div>
                          <div style={{ fontSize: 12, marginTop: 6 }}>{n.type}</div>
                        </div>
                      ))}
                    </div>
                    <div style={{ marginTop: 10, display: 'flex', gap: 8 }}>
                      <button className="btn-sm" onClick={() => setFlowchartView('guard')}>Guard</button>
                      <button className="btn-sm" onClick={() => setFlowchartView('pass')}>Pass</button>
                      <button className="btn-sm" onClick={() => setFlowchartView('takedown')}>Takedown</button>
                    </div>
                    <div style={{ marginTop: 10 }}>
                      <button className="btn-primary-portal" onClick={() => { setEditMode(true); }}>Open Flowchart Editor</button>
                    </div>
                  </div>
                )}

                {activeTab === 'store' && (
                  <div>
                    <h3 style={{ marginTop: 0 }}>Store</h3>
                    <div style={{ marginTop: 8 }}>
                      <p style={{ color: 'var(--portal-muted)', marginTop: 0 }}>Browse academy gear and coaching sessions. Tap Open Store to view products and checkout.</p>
                      <div style={{ marginTop: 8 }}>
                        <button className="btn-primary-portal" onClick={() => setShowStoreModal(true)}>Open Store</button>
                      </div>
                    </div>
                  </div>
                )}

                {activeTab === 'purchases' && (
                  <div>
                    <StudentPurchaseHistory
                      wallet={(selectedWalletForView || walletAddr)}
                      student={student}
                      orderHistory={Array.isArray(student?.orderHistory) ? student.orderHistory : []}
                      onViewCollectable={(order) => {
                      try {
                        // Open store modal and dispatch event so other components can react
                        setActiveTab('store');
                        setShowStoreModal(true);
                        window.dispatchEvent(new CustomEvent('openCollectable', { detail: { order } }));
                      } catch (e) { console.warn('onViewCollectable failed', e); }
                    }} />
                  </div>
                )}

                {activeTab === 'collectables' && (
                  <div>
                    <StudentCollectables wallet={(selectedWalletForView || walletAddr)} student={student} orderHistory={Array.isArray(student?.orderHistory) ? student.orderHistory : []} />
                  </div>
                )}

                {activeTab === 'messages' && (
                  <div>
                    <h3 style={{ marginTop: 0 }}>Messages</h3>
                    <div style={{ maxHeight: 240, overflowY: 'auto' }}>
                      {(messages || []).slice().reverse().map(msg => (
                        <div key={msg.id} style={{ padding: 8, borderRadius: 8, background: msg.from === 'admin' ? '#0b1220' : '#071322', marginBottom: 8 }}>
                          <div style={{ fontWeight: 700 }}>{msg.from === 'admin' ? 'Admin' : 'You'}</div>
                          <div style={{ fontSize: 12, marginTop: 4 }}>{msg.body}</div>
                          <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 6 }}>{new Date(msg.timestamp).toLocaleString()}</div>
                        </div>
                      ))}
                      {(!messages || messages.length === 0) && (<p className="empty-state">No messages yet.</p>)}
                    </div>
                    <div style={{ marginTop: 8 }}>
                      <textarea value={newMessage} onChange={(e) => setNewMessage(e.target.value)} placeholder="Write a message..." rows={2} style={{ width: '100%', padding: 8, borderRadius: 6 }} />
                      <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 6 }}>
                        <button className="btn-sm" onClick={sendMessage} disabled={!newMessage.trim() || sendingMessage}>{sendingMessage ? 'Sending...' : 'Send'}</button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Mobile Flowchart Editor Modal (reuses flowchart state & handlers) */}
          {editMode && (
            <div className="modal-overlay" onClick={() => setEditMode(false)}>
              <div className="modal-content flowchart-modal" onClick={(e) => e.stopPropagation()}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12 }}>
                  <h3 style={{ margin: 0 }}>Flowchart Editor</h3>
                  <div style={{ display: 'flex', gap: 8 }}>
                    <button className="btn-sm btn-success" onClick={saveFlowchart}>Save</button>
                    <button className="btn-sm btn-cancel" onClick={cancelEdit}>Close</button>
                  </div>
                </div>

                <div style={{ marginTop: 12 }} className="flowchart-view-tabs">
                  <button className={`view-tab ${flowchartView === 'guard' ? 'active' : ''}`} onClick={() => setFlowchartView('guard')}>Guard</button>
                  <button className={`view-tab ${flowchartView === 'pass' ? 'active' : ''}`} onClick={() => setFlowchartView('pass')}>Pass</button>
                  <button className={`view-tab ${flowchartView === 'takedown' ? 'active' : ''}`} onClick={() => setFlowchartView('takedown')}>Takedown</button>
                </div>

                <div style={{ marginTop: 10 }} className="flowchart-toolbar">
                  <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
                    <button className="btn-sm" onClick={() => addTechniqueNode('guard')}>+ Guard</button>
                    <button className="btn-sm" onClick={() => addTechniqueNode('pass')}>+ Pass</button>
                    <button className="btn-sm" onClick={() => addTechniqueNode('takedown')}>+ Takedown</button>
                    <button className="btn-sm" onClick={() => addTechniqueNode('submission')}>+ Submission</button>
                  </div>
                  <div style={{ display: 'flex', gap: 6, alignItems: 'center', marginTop: 8 }}>
                    <div style={{ fontSize: 13, color: '#cbd5e1', marginRight: 6 }}>View:</div>
                    <button className="btn-sm" onClick={zoomOut}>−</button>
                    <div style={{ minWidth: 36, textAlign: 'center' }}>{flowZoom}x</div>
                    <button className="btn-sm" onClick={zoomIn}>+</button>
                    <button className="btn-sm" onClick={resetZoom}>Reset</button>
                    <div style={{ width: 1, height: 24, background: 'rgba(255,255,255,0.03)', margin: '0 8px' }} />
                    <button className="btn-sm" onClick={() => panBy(-40, 0)}>◀</button>
                    <button className="btn-sm" onClick={() => panBy(40, 0)}>▶</button>
                    <button className="btn-sm" onClick={() => panBy(0, -30)}>▲</button>
                    <button className="btn-sm" onClick={() => panBy(0, 30)}>▼</button>
                  </div>
                </div>

                <div style={{ marginTop: 12 }}>
                  <svg className="flowchart-canvas" viewBox="0 0 1000 700" onMouseMove={handleMouseMove} onMouseUp={handleMouseUp} onMouseLeave={handleMouseUp} onTouchStart={handleTouchStart} onTouchMove={handleTouchMove} onTouchEnd={handleTouchEnd} style={{ touchAction: 'none' }}>
                    <defs>
                      <marker id="arrowhead" markerWidth="10" markerHeight="10" refX="9" refY="3" orient="auto">
                        <polygon points="0 0, 10 3, 0 6" fill="rgba(255,255,255,0.6)" />
                      </marker>
                    </defs>
                    <g transform={`translate(${flowPan.x},${flowPan.y}) scale(${flowZoom})`}>
                    {flowchartData[flowchartView].edges.map((edge, idx) => {
                      const fromNode = flowchartData[flowchartView].nodes.find(n => n.id === edge.from);
                      const toNode = flowchartData[flowchartView].nodes.find(n => n.id === edge.to);
                      if (!fromNode || !toNode) return null;
                      const fw = fromNode.width || 150;
                      const fh = fromNode.height || 50;
                      const tw = toNode.width || 150;
                      const th = toNode.height || 50;
                      const x1 = fromNode.x + fw / 2;
                      const y1 = fromNode.y + fh / 2;
                      const x2 = toNode.x + tw / 2;
                      const y2 = toNode.y + th / 2;
                      return (
                        <g key={idx}>
                          <line x1={x1} y1={y1} x2={x2} y2={y2} stroke="rgba(255,255,255,0.4)" strokeWidth="2" markerEnd="url(#arrowhead)" />
                        </g>
                      );
                    })}
                    {flowchartData[flowchartView].nodes.map(node => {
                      const w = node.width || 150;
                      const h = node.height || 50;
                      const fill = node.fillColor || (node.type === 'guard' ? 'rgba(59, 130, 246, 0.3)' : node.type === 'pass' ? 'rgba(168, 85, 247, 0.3)' : node.type === 'takedown' ? 'rgba(34, 197, 94, 0.3)' : 'rgba(239, 68, 68, 0.3)');
                      return (
                        <g key={node.id} className={`flowchart-node ${node.type} ${selectedNode?.id === node.id ? 'selected' : ''}`} style={{ cursor: 'grab' }} onMouseDown={(e) => handleNodeMouseDown(e, node)} onTouchStart={(e) => handleTouchStartNode(e, node)}>
                          <rect x={node.x} y={node.y} width={w} height={h} rx="8" fill={fill} stroke={selectedNode?.id === node.id ? '#fbbf24' : 'rgba(255,255,255,0.5)'} strokeWidth={selectedNode?.id === node.id ? '3' : '2'} onClick={() => handleNodeClick(node)} />
                          <text x={node.x + w / 2} y={node.y + h / 2 + 5} textAnchor="middle" fill="white" fontSize="14" fontWeight="500" pointerEvents="none">{node.label}</text>
                          {/* Small edit button (top-right) - touch-friendly */}
                          <g
                            transform={`translate(${node.x + w - 14}, ${node.y + 8})`}
                            onClick={(e) => { e.stopPropagation(); e.preventDefault(); setSelectedNode(node); }}
                            onTouchStart={(e) => { e.stopPropagation(); e.preventDefault(); setSelectedNode(node); }}
                            style={{ cursor: 'pointer', touchAction: 'manipulation' }}
                          >
                            <circle cx={0} cy={0} r={10} fill={selectedNode?.id === node.id ? '#f59e0b' : 'rgba(0,0,0,0.35)'} stroke="rgba(255,255,255,0.6)" strokeWidth="1" />
                            <text x={0} y={3} textAnchor="middle" fill="white" fontSize="12" fontWeight="700" pointerEvents="none">✎</text>
                          </g>
                        </g>
                      );
                    })}
                    </g>
                  </svg>
                </div>

                {selectedNode && (
                  <div className="flowchart-editor" style={{ marginTop: 10 }}>
                    <h4>Edit Node</h4>
                    <div className="editor-field">
                      <label>Name</label>
                      <input type="text" value={selectedNode.label} onChange={(e) => updateNodeLabel(selectedNode.id, e.target.value)} />
                    </div>
                    <div className="editor-field" style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: 8 }}>
                      <div style={{ flex: 1 }}>
                        <label>Width</label>
                        <input type="number" min={40} value={selectedNode.width || 150} onChange={(e) => updateNodeProps(selectedNode.id, { width: Math.max(40, Number(e.target.value || 0)) })} />
                      </div>
                      <div style={{ flex: 1 }}>
                        <label>Height</label>
                        <input type="number" min={24} value={selectedNode.height || 50} onChange={(e) => updateNodeProps(selectedNode.id, { height: Math.max(24, Number(e.target.value || 0)) })} />
                      </div>
                      <div style={{ width: 84 }}>
                        <label>Color</label>
                        <input type="color" value={selectedNode.fillColor ? rgbToHex(selectedNode.fillColor) : '#3b82f6'} onChange={(e) => updateNodeProps(selectedNode.id, { fillColor: e.target.value })} />
                      </div>
                    </div>
                    <div style={{ marginTop: 8, display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                      <div style={{ fontSize: 12, color: 'var(--portal-muted)', marginRight: 8 }}>Palette:</div>
                      {['#3b82f6','#a855f7','#22c55e','#ef4444','#f59e0b','#06b6d4','#8b5cf6','#ef9a9a'].map(c => (
                        <button
                          key={c}
                          className={`color-swatch ${(selectedNode.fillColor && rgbToHex(selectedNode.fillColor).toLowerCase() === c) ? 'selected' : ''}`}
                          onClick={() => updateNodeProps(selectedNode.id, { fillColor: c })}
                          aria-label={`Select color ${c}`}
                          style={{ background: c }}
                        />
                      ))}
                    </div>
                    <div className="editor-field">
                      <label>Connect To</label>
                      <div className="connect-buttons">
                        {flowchartData[flowchartView].nodes.filter(n => n.id !== selectedNode.id).map(n => (
                          <button key={n.id} className={`btn-xs ${flowchartData[flowchartView].edges.some(e => e.from === selectedNode.id && e.to === n.id) ? 'active' : ''}`} onClick={() => toggleEdge(selectedNode.id, n.id)}>{n.label}</button>
                        ))}
                      </div>
                    </div>
                    {selectedNode.editable && <button className="btn-danger btn-sm" onClick={() => deleteNode(selectedNode.id)}>Delete</button>}
                  </div>
                )}
              </div>
            </div>
          )}

          {activeTab === 'store' && (
            <div className="tab-panel">
              <h3>Store</h3>
              <div style={{ marginTop: 8 }} className="store-embedded">
                <StoreOrder />
              </div>
            </div>
          )}

          {/* Removed duplicate bottom semi-menu for mobile (top header already provides Confirm / Open Portal / Main Menu) */}
          {/* Store modal (mobile) */}
          {showStoreModal && (
            <div className="cc-modal-overlay" onClick={() => setShowStoreModal(false)}>
              <div className="cc-modal" onClick={(e) => e.stopPropagation()}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <h3 style={{ margin: 0 }}>Store</h3>
                  <button className="btn-sm" onClick={() => setShowStoreModal(false)}>Close</button>
                </div>
                <div style={{ marginTop: 10 }}>
                  <StoreOrder onClose={() => setShowStoreModal(false)} />
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }
  
  if (isAdmin && allStudentsWithWallet.length === 0) {
    return (
      <div className="student-portal-overlay" onClick={handleClose}>
        <div className="student-portal-container" onClick={(e) => e.stopPropagation()}>
          <button className="portal-close-btn" onClick={handleClose}>×</button>
          <div className="portal-error">
            <div className="admin-badge-supreme">👑 ADMIN MODE</div>
            <h2>No Students with Wallets</h2>
            <p className="error-message">No students have wallet addresses linked yet.</p>
            <div className="error-instructions">
              <p><strong>To link student wallets:</strong></p>
              <ol>
                <li>Go to Control Center → Students</li>
                <li>Click on a student profile</li>
                <li>Use the "Link Wallet" button</li>
                <li>Have the student sign with their MetaMask</li>
              </ol>
            </div>
            <div style={{ display: 'flex', gap: 10, marginTop: 12 }}>
              <button
                className="btn-primary-portal"
                onClick={async () => {
                  try {
                    // Attempt robust fetch (force) and notify other tabs
                    localStorage.setItem('braga_force_pull', Date.now().toString());
                    const data = await fetchAggregatedData({ force: true, retries: 4, backoff: 400 });
                    const studs = data ? (Array.isArray(data) ? data : (data.students || [])) : [];
                    const studentsWithWallet = (studs || [])
                      .map(s => ({ ...s, walletAddress: (s.walletAddress || s.wallet || s.wallet_address || s.address || '').toString().toLowerCase() }))
                      .filter(s => s.walletAddress);
                    if (studentsWithWallet.length > 0) {
                      setAllStudentsWithWallet(studentsWithWallet);
                      const adminMap = {};
                      studentsWithWallet.forEach(s => { if (s.isAdmin) adminMap[s.walletAddress] = s; });
                      setAdminStudentsMap(adminMap);
                      const seeded = studentsWithWallet.find(s => (s.id || '').toString().toLowerCase() === 'samuel-braga' || (s.id || '').toString().toLowerCase() === 'samuel_braga');
                      if (seeded) {
                        setSelectedStudentId(seeded.id);
                        setStudent(seeded);
                      } else {
                        setSelectedStudentId(studentsWithWallet[0].id);
                      }
                      window.dispatchEvent(new CustomEvent('students-synced', { detail: { students: studs } }));
                    } else {
                      alert('No linked wallets were found on the server.');
                    }
                  } catch (e) {
                    console.warn('Failed to refresh students from server:', e);
                    alert('Failed to refresh students from server. See console for details.');
                  }
                }}
              >
                Refresh from server
              </button>

              <button className="btn-primary-portal" onClick={handleClose}>Close</button>
            </div>
          </div>
        </div>
      
      </div>
    );
  }

  return (
    <div className="student-portal-overlay" onClick={handleClose}>
      <div 
        className={`student-portal-container ${
          student && student.id === 'samuel-braga' ? 'golden-profile' : ''
        } ${isMobile ? 'mobile-mode' : 'desktop-mode'} ${
          // apply admin badge border class (visual only)
          (student && student.adminBadge && (ADMIN_BADGES && ADMIN_BADGES[student.adminBadge]) ) ? (ADMIN_BADGES[student.adminBadge].borderClass) : ''
        }`} 
        onClick={(e) => e.stopPropagation()}
      >
  <WalletFloating />
  <button className="portal-close-btn" onClick={handleClose}>×</button>
        
        <div className="portal-header">
          <h2>🎓 Student Portal</h2>
          {isAdmin && (
            <div className="admin-badge-supreme">
              👑 FULL ACCESS SUPREME
            </div>
          )}
          {!isAdmin && adminWallets.length > 0 && (
            <div style={{ marginTop: 8 }}>
              <button
                className="btn-admin-connect"
                onClick={handleAdminConnect}
                disabled={connectingWallet}
              >
                {connectingWallet ? 'Connecting…' : 'Connect MetaMask (Admin)'}
              </button>
            </div>
          )}
          <p className="portal-subtitle">
            {isAdmin ? 'Admin supervision - viewing all registered students' : 'Your training journey & performance'}
          </p>
          {isMobile && student && (
            <div className="mobile-header-actions" style={{ marginTop: 10, display: 'flex', flexDirection: 'column', gap: 8 }}>
              <div style={{ fontWeight: 700, fontSize: 18 }}>{student.name || `${student.firstName || ''} ${student.lastName || ''}`.trim()}</div>
              <div style={{ display: 'flex', gap: 8 }}>
                <button
                  className={`btn-sm wallet-link-button ${student && student.walletAddress ? 'connected' : 'disconnected'}`}
                  onClick={async (e) => { e.stopPropagation(); await requestAndOpen(); }}
                  disabled={connectingWallet}
                >
                  {connectingWallet ? 'Connecting…' : (student && student.walletAddress ? 'Confirm Wallet' : 'Connect Wallet')}
                </button>
                <button className="btn-sm" onClick={() => openStudentPortal(student.id)}>
                  Open Portal
                </button>
                <button className="btn-sm" onClick={handleClose}>
                  Main Menu
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Admin: Student Selector */}
        {isAdmin && allStudentsWithWallet.length > 0 && (
          <div className="admin-student-selector">
            <label>Select Student:</label>
            <select 
              value={selectedStudentId || ''}
              onChange={(e) => setSelectedStudentId(e.target.value)}
              className="student-select-dropdown"
            >
              {allStudentsWithWallet.map(s => (
                <option key={s.id} value={s.id}>
                  {s.name || `${s.firstName} ${s.lastName}`} ({s.belt || 'No Belt'}) - {s.walletAddress?.slice(0, 6)}...{s.walletAddress?.slice(-4)}
                </option>
              ))}
            </select>
            <div className="student-count-badge">
              {allStudentsWithWallet.length} student{allStudentsWithWallet.length !== 1 ? 's' : ''} with wallet
            </div>
          </div>
        )}

        {!student && (
          <div className="portal-content">
            <div className="portal-loading">
              <div className="loading-spinner" />
              <p>Loading student profile...</p>
            </div>
          </div>
        )}

        {student && (
          <>
        {/* Quick Stats */}
        <div className="portal-stats">
          <div className="stat-card">
            <div className="stat-icon">🥋</div>
            <div className="stat-value">{stats.totalClasses}</div>
            <div className="stat-label">Total Classes</div>
          </div>
          <div className="stat-card">
            <div className="stat-icon">📅</div>
            <div className="stat-value">{stats.thisMonth}</div>
            <div className="stat-label">This Month</div>
          </div>
          <div className="stat-card">
            <div className="stat-icon">🔥</div>
            <div className="stat-value">{stats.streak}</div>
            <div className="stat-label">Day Streak</div>
          </div>
          <div className="stat-card">
            <div className={`stat-icon payment-${stats.paymentStatus}`}>💳</div>
            <div className="stat-value">{stats.paymentStatus === 'paid' ? '✓' : (stats.daysOverdue || 0)}</div>
            <div className="stat-label">{stats.paymentStatus === 'paid' ? 'Paid' : 'Days Overdue'}</div>
          </div>
        </div>

        {/* Tabs */}
        <div className="portal-tabs">
          <button 
            className={`tab ${activeTab === 'profile' ? 'active' : ''}`}
            onClick={() => setActiveTab('profile')}
          >
            Profile
          </button>
          <button 
            className={`tab ${activeTab === 'attendance' ? 'active' : ''}`}
            onClick={() => setActiveTab('attendance')}
          >
            Attendance
          </button>
          <button 
            className={`tab ${activeTab === 'belt' ? 'active' : ''}`}
            onClick={() => setActiveTab('belt')}
          >
            Belt Progress
          </button>
          <button 
            className={`tab ${activeTab === 'awards' ? 'active' : ''}`}
            onClick={() => setActiveTab('awards')}
          >
            Awards
          </button>
          <button 
            className={`tab ${activeTab === 'payments' ? 'active' : ''}`}
            onClick={() => setActiveTab('payments')}
          >
            Payments
          </button>
          <button 
            className={`tab ${activeTab === 'occurring' ? 'active' : ''}`}
            onClick={() => setActiveTab('occurring')}
          >
            Occurring Payments
          </button>
          <button 
            className={`tab ${activeTab === 'store' ? 'active' : ''}`}
            onClick={() => setActiveTab('store')}
          >
            Store
          </button>
          <button 
            className={`tab ${activeTab === 'purchases' ? 'active' : ''}`}
            onClick={() => setActiveTab('purchases')}
          >
            Purchase History
          </button>
          <button 
            className={`tab ${activeTab === 'collectables' ? 'active' : ''}`}
            onClick={() => setActiveTab('collectables')}
          >
            Collectables
          </button>
          <button 
            className={`tab ${activeTab === 'messages' ? 'active' : ''}`}
            onClick={() => setActiveTab('messages')}
          >
            Messages {messages.filter(m => !m.read && m.from === 'admin').length > 0 && (
              <span className="badge">{messages.filter(m => !m.read && m.from === 'admin').length}</span>
            )}
          </button>
          <button 
            className={`tab ${activeTab === 'flowchart' ? 'active' : ''}`}
            onClick={() => setActiveTab('flowchart')}
          >
            Flowchart
          </button>
        </div>

        {/* Tab Content */}
        <div className="portal-content">
          {activeTab === 'profile' && (
            <div className="tab-panel">
              <div className="profile-header-actions" style={{display:'flex',alignItems:'center',justifyContent:'space-between',gap:12}}>
                  <div style={{display:'flex',gap:12,alignItems:'center'}}>
                    <h3 style={{margin:0}}>Student Profile</h3>
                  </div>

                  <div style={{display:'flex',alignItems:'center',gap:12}}>
                      <div style={{marginRight:8}}>
                        {/* Explicit portal badge wrapper to ensure visibility/layout */}
                        <div className="portal-badge-wrapper">
                          <h3 className="portal-section-title">Achievements</h3>
                          <div className="portal-badge-row" style={{ overflowX: 'auto' }}>
                            {(Array.isArray(student?.badges) ? student.badges : []).filter(b => b?.image).map((b) => (
                              <img
                                key={`${b.id}-${badgesAnimateKey || 0}`}
                                src={b.image}
                                alt={b?.id || b?.name || 'badge'}
                                className={`portal-badge-icon ${badgesAnimateKey ? 'portal-badge-animated' : ''}`}
                                onError={(e) => { e.currentTarget.style.opacity = '0.6'; }}
                              />
                            ))}
                          </div>
                        </div>
                      </div>
                    <button 
                      className="btn-edit-profile"
                      onClick={() => {
                        setEditedProfile({ ...student });
                        setIsCreating(false);
                        setShowEditModal(true);
                      }}
                    >
                      ✏️ Edit Profile
                    </button>
                  </div>
                </div>
              <div className="profile-grid">
                <div className="profile-field">
                  <label>Full Name</label>
                  <div className="field-value">
                    {student.name || `${student.firstName || ''} ${student.lastName || ''}`.trim() || '—'}
                  </div>
                </div>
                <div className="profile-field">
                  <label>Email</label>
                  <div className="field-value">{student.email || '—'}</div>
                </div>
                <div className="profile-field">
                  <label>Phone</label>
                  <div className="field-value">{student.phone || '—'}</div>
                </div>
                <div className="profile-field">
                  <label>Belt Rank</label>
                  <div className="field-value belt-badge">{student.belt || 'White'}</div>
                </div>
                <div className="profile-field">
                  <label>Member Since</label>
                  <div className="field-value">
                    {student.joinDate ? new Date(student.joinDate).toLocaleDateString() : '—'}
                  </div>
                </div>
                <div className="profile-field">
                  <label>Membership Type</label>
                  <div className="field-value">{student.membershipType || '—'}</div>
                </div>
                <div className="profile-field full-width">
                  <label>Address</label>
                  <div className="field-value">{student.address || '—'}</div>
                </div>
                <div className="profile-field full-width">
                  <label>Emergency Contact</label>
                  <div className="field-value">{student.emergencyContact || '—'}</div>
                </div>
                <div className="profile-field full-width">
                  <label>Wallet Address</label>
                  <div className="field-value mono">
                    {student.walletAddress ? (
                      <button
                        className="wallet-link-button"
                        onClick={async (e) => {
                          e.stopPropagation();
                          try {
                            const addr = await openWallet();
                            if (!addr) return;
                            const lower = addr.toLowerCase();

                            // If the connected wallet matches the current student, confirm
                            const studentWallet = student?.walletAddress || '';
                            if (studentWallet && studentWallet.toLowerCase() === lower) {
                              alert('Wallet confirmed — welcome back to the Student Portal!');
                              // Broadcast update
                              window.dispatchEvent(new CustomEvent('student-updated', { detail: { ...student, walletAddress: addr } }));
                              return;
                            }

                            // If wallet doesn't match, offer to join the portal as a new student using this wallet
                            const join = window.confirm('This wallet is not linked to the current profile. Do you want to join the portal as a new student with this wallet?');
                            if (!join) return;

                            // Create a minimal student record and persist locally and to the API (best-effort)
                            const newId = `student_${Date.now()}`;
                            const newStudent = {
                              id: newId,
                              walletAddress: addr,
                              joinDate: new Date().toISOString(),
                              name: 'New Student',
                              firstName: 'New',
                              lastName: 'Student'
                            };

                            try {
                              const raw = localStorage.getItem('students');
                              const studs = raw ? JSON.parse(raw) : [];
                              studs.push(newStudent);
                              localStorage.setItem('students', JSON.stringify(studs));
                              setStudent(newStudent);
                              setSelectedStudentId(newId);

                              // Broadcast sync events so other tabs / admin UI update
                              window.dispatchEvent(new CustomEvent('students-synced', { detail: { students: studs } }));
                              window.dispatchEvent(new CustomEvent('student-updated', { detail: newStudent }));

                              // Best-effort API persist
                              try {
                                await apiFetch('/api/students', {
                                  method: 'POST',
                                  body: JSON.stringify(newStudent)
                                });
                              } catch (err) {
                                console.warn('Failed to POST new student to API (best-effort):', err);
                              }

                              alert('Joined the Student Portal as a new student. You can update your profile in Edit Profile.');
                            } catch (err) {
                              console.error('Failed to join as new student:', err);
                              alert('Failed to join as new student. See console for details.');
                            }
                          } catch (err) {
                            console.warn('Wallet confirmation flow failed', err);
                          }
                        }}
                      >
                        {student?.walletAddress?.slice(0, 6)}…{student?.walletAddress?.slice(-4)}
                      </button>
                    ) : (
                      <button
                        className="wallet-link-button"
                        onClick={async (e) => { e.stopPropagation(); await openWallet(); }}
                      >
                        Connect Wallet
                      </button>
                    )}
                  </div>
                </div>
                {student.notes && (
                  <div className="profile-field full-width">
                    <label>Notes</label>
                    <div className="field-value">{student.notes}</div>
                  </div>
                )}
              </div>
            </div>
          )}

          {activeTab === 'attendance' && (
            <div className="tab-panel">
              <h3>Attendance History</h3>
              {renderAttendanceProgress()}
              <div className="attendance-summary">
                <p>Total classes attended: <strong>{stats.totalClasses}</strong></p>
                <p>This month: <strong>{stats.thisMonth}</strong> classes</p>
                <p>This week: <strong>{stats.thisWeek}</strong> classes</p>
                {stats.lastAttendance && (
                  <p>Last class: <strong>{stats.lastAttendance.toLocaleDateString()}</strong></p>
                )}
              </div>
              <div className="attendance-list">
                {attendance.length === 0 ? (
                  <p className="empty-state">No attendance records yet. Start training!</p>
                ) : (
                  attendance.slice().reverse().map((ts, idx) => {
                    const date = new Date(ts);
                    return (
                      <div key={idx} className="attendance-item">
                        <div className="attendance-icon">✓</div>
                        <div className="attendance-details">
                          <div className="attendance-date">{date.toLocaleDateString()}</div>
                          <div className="attendance-time">{date.toLocaleTimeString()}</div>
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
              <div style={{ marginTop: 12 }}>
                <button className="btn-primary-portal" onClick={handleCheckIn}>Check In</button>
              </div>
            </div>
          )}

            {/* Wallet-linked students modal (admin mobile crown) */}
            {showWalletList && (
              <div className="wallet-list-overlay" onClick={() => setShowWalletList(false)}>
                <div className="wallet-list-panel" onClick={(e) => e.stopPropagation()}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div style={{ fontWeight: 800 }}>Wallet-Connected Students</div>
                    <button className="portal-close-btn" onClick={() => setShowWalletList(false)}>×</button>
                  </div>
                  <div style={{ marginTop: 12 }}>
                    {Array.isArray(allStudentsWithWallet) && allStudentsWithWallet.length > 0 ? (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                        {allStudentsWithWallet.map(s => {
                          // collect unique wallet addresses for this student
                          const addrs = [];
                          [s.walletAddress, s.wallet, s.address].forEach(a => { try { if (a && String(a).trim()) addrs.push(String(a).toLowerCase()); } catch (e) {} });
                          const uniq = [...new Set(addrs)];
                          return (
                            <div key={s.id} style={{ padding: '8px 6px', borderBottom: '1px solid rgba(0,0,0,0.06)' }}>
                              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <div>
                                  <div style={{ fontWeight: 700 }}>{s.name || `${s.firstName || ''} ${s.lastName || ''}`.trim()}</div>
                                  <div style={{ fontSize: 12, color: 'var(--portal-muted)' }}>{uniq.join(' • ')}</div>
                                </div>
                                <div style={{ display: 'flex', gap: 8 }}>
                                  <button className="btn-sm" onClick={() => { setSelectedStudentId(s.id); setStudent(s); setSelectedWalletForView(''); setShowWalletList(false); }}>Load Profile</button>
                                  <div style={{ position: 'relative' }}>
                                    <button className="btn-sm" onClick={() => { setSelectedStudentId(s.id); setStudent(s); setActiveTab('store'); setSelectedWalletForView(''); setShowWalletList(false); }}>View Purchases</button>
                                  </div>
                                </div>
                              </div>
                              {uniq.length > 0 && (
                                <div style={{ marginTop: 8, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                                  {uniq.map(a => (
                                    <button key={a} className="btn-sm" onClick={() => { setSelectedStudentId(s.id); setStudent(s); setSelectedWalletForView(a); setActiveTab('store'); setShowWalletList(false); }} title={`View purchases for ${a}`}>{a.slice(0,8)}...{a.slice(-6)}</button>
                                  ))}
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    ) : (
                      <div className="empty-state">No wallet-linked students found.</div>
                    )}
                  </div>
                </div>
              </div>
            )}

          {activeTab === 'belt' && (
            <div className="tab-panel">
              <h3>Belt Progression</h3>
              <div className="belt-journey">
                {beltProgression.map(({ belt, completed, current, future }) => (
                  <div 
                    key={belt} 
                    className={`belt-stage ${completed ? 'completed' : ''} ${current ? 'current' : ''} ${future ? 'future' : ''}`}
                  >
                    <div className="belt-icon" style={{ background: getBeltColor(belt) }}>
                      {completed && '✓'}
                      {current && '●'}
                    </div>
                    <div className="belt-name">{belt.charAt(0).toUpperCase() + belt.slice(1)} Belt</div>
                    {current && (
                      <div className="belt-current-badge">Current Rank</div>
                    )}
                  </div>
                ))}
              </div>
              <div className="belt-info">
                <h4>Your Journey</h4>
                <p>You are currently a <strong>{student.belt || 'White'} Belt</strong></p>
                <p>Total classes attended: <strong>{stats.totalClasses}</strong></p>
                <p>Keep training to progress to the next level!</p>
              </div>
            </div>
          )}

          {activeTab === 'awards' && (
            <div className="tab-panel">
              <h3>Awards & Achievements</h3>
              <div className="awards-grid">
                {stats.totalClasses >= 10 && (
                  <div className="award-card">
                    <div className="award-icon">🥋</div>
                    <div className="award-title">10 Classes</div>
                    <div className="award-desc">Completed 10 training sessions</div>
                  </div>
                )}
                {stats.totalClasses >= 50 && (
                  <div className="award-card">
                    <div className="award-icon">🏆</div>
                    <div className="award-title">50 Classes</div>
                    <div className="award-desc">Half-century achievement</div>
                  </div>
                )}
                {stats.totalClasses >= 100 && (
                  <div className="award-card">
                    <div className="award-icon">💎</div>
                    <div className="award-title">100 Classes</div>
                    <div className="award-desc">Century club member</div>
                  </div>
                )}
                {stats.streak >= 7 && (
                  <div className="award-card">
                    <div className="award-icon">🔥</div>
                    <div className="award-title">7-Day Streak</div>
                    <div className="award-desc">Trained 7 days in a row</div>
                  </div>
                )}
                {stats.paymentStatus === 'paid' && (
                  <div className="award-card">
                    <div className="award-icon">💳</div>
                    <div className="award-title">Good Standing</div>
                    <div className="award-desc">Payments up to date</div>
                  </div>
                )}
              
              </div>
              {stats.totalClasses < 10 && (
                <p className="empty-state">Keep training to unlock more achievements!</p>
              )}
              {renderTrophies()}
              {renderProgressControls()}
            </div>
          )}

          {activeTab === 'payments' && (
            <div className="tab-panel">
              <h3>Payments & Membership</h3>
              
              {/* Current Status */}
              <div className="payment-status-card">
                <div className={`payment-badge payment-${stats.paymentStatus}`}>
                  {stats.paymentStatus === 'paid' ? '✓ Paid' : 
                   stats.paymentStatus === 'due' ? '⚠ Due Soon' : 
                   '❌ Overdue'}
                </div>
                {student.lastPaymentDate && (
                  <div className="payment-details">
                    <p>Last payment: <strong>{new Date(student.lastPaymentDate).toLocaleDateString()}</strong></p>
                    <p>Days since payment: <strong>{stats.daysOverdue}</strong></p>
                  </div>
                )}
                {!student.lastPaymentDate && (
                  <div className="payment-details">
                    <p>No payment records found.</p>
                  </div>
                )}
              </div>

              {/* Payment Plans */}
              <div className="payment-plans">
                <div className="payment-header-row">
                  <div>
                    <h4>💳 Make a Payment</h4>
                    <p className="payment-subtitle">Pay securely with MetaMask</p>
                  </div>
                  
                  {/* Currency Toggle */}
                  <div className="currency-toggle">
                    <button 
                      className={`currency-btn ${paymentCurrency === 'USD' ? 'active' : ''}`}
                      onClick={() => setPaymentCurrency('USD')}
                    >
                      USD $
                    </button>
                    <button 
                      className={`currency-btn ${paymentCurrency === 'ETH' ? 'active' : ''}`}
                      onClick={() => setPaymentCurrency('ETH')}
                    >
                      ETH Ξ
                    </button>
                  </div>
                </div>

                {/* Payment sub-tabs for quick selection (desktop + mobile friendly) */}
                <div className="payment-subtabs" style={{ display: 'flex', gap: 8, marginTop: 12, flexWrap: 'nowrap', overflowX: 'auto' }}>
                  {['weekly','biweekly','monthly','threeMonth','custom'].map(p => (
                    <button
                      key={p}
                      className={`subtab-btn ${selectedPlan === p ? 'active' : ''}`}
                      onClick={() => setSelectedPlan(p)}
                      style={{ minWidth: 96, flex: '0 0 auto' }}
                    >
                      {p === 'weekly' ? 'Weekly $39' : p === 'biweekly' ? 'Bi-Weekly $78' : p === 'monthly' ? 'Monthly $139' : p === 'threeMonth' ? '3 Months $339' : 'Custom'}
                    </button>
                  ))}
                </div>

                {ethPrice && (
                  <div className="eth-price-display">
                    <span className="eth-icon">Ξ</span>
                    1 ETH = ${ethPrice.toLocaleString()} USD
                    <span className="live-indicator">● LIVE</span>
                  </div>
                )}

                {loadingEthPrice && (
                  <div className="eth-price-loading">Loading live ETH price...</div>
                )}
                
                <div className="plans-grid">
                  <div 
                    className={`plan-card ${selectedPlan === 'weekly' ? 'selected' : ''}`}
                    onClick={() => setSelectedPlan('weekly')}
                  >
                    <div className="plan-header">
                      <div className="plan-icon">📅</div>
                      <div className="plan-name">Weekly</div>
                    </div>
                    <div className="plan-price">
                      {paymentCurrency === 'USD' ? '$39' : 
                       ethPrice ? `${(39 / ethPrice).toFixed(6)} ETH` : '...'
                      }
                    </div>
                    <div className="plan-desc">
                      {paymentCurrency === 'USD' ? 'per week' : 
                       ethPrice ? `≈ $39 USD` : 'per week'
                      }
                    </div>
                    <ul className="plan-features">
                      <li>✓ Unlimited classes</li>
                      <li>✓ Flexible commitment</li>
                      <li>✓ Auto-reminder</li>
                    </ul>
                  </div>

                  <div 
                    className={`plan-card ${selectedPlan === 'biweekly' ? 'selected' : ''}`}
                    onClick={() => setSelectedPlan('biweekly')}
                  >
                    <div className="plan-header">
                      <div className="plan-icon">💎</div>
                      <div className="plan-name">Bi-Weekly</div>
                    </div>
                    <div className="plan-price">
                      {paymentCurrency === 'USD' ? '$78' : 
                       ethPrice ? `${(78 / ethPrice).toFixed(6)} ETH` : '...'
                      }
                    </div>
                    <div className="plan-desc">
                      {paymentCurrency === 'USD' ? 'per 2 weeks' : 
                       ethPrice ? `≈ $78 USD` : 'per 2 weeks'
                      }
                    </div>
                    <ul className="plan-features">
                      <li>✓ Unlimited classes</li>
                      <li>✓ Best value</li>
                      <li>✓ Auto-reminder</li>
                    </ul>
                  </div>
                  <div 
                    className={`plan-card ${selectedPlan === 'monthly' ? 'selected' : ''}`}
                    onClick={() => setSelectedPlan('monthly')}
                  >
                    <div className="plan-header">
                      <div className="plan-icon">🗓️</div>
                      <div className="plan-name">Monthly</div>
                    </div>
                    <div className="plan-price">
                      {paymentCurrency === 'USD' ? '$139' : 
                       ethPrice ? `${(139 / ethPrice).toFixed(6)} ETH` : '...'
                      }
                    </div>
                    <div className="plan-desc">
                      {paymentCurrency === 'USD' ? 'per month' : 
                       ethPrice ? `≈ $139 USD` : 'per month'
                      }
                    </div>
                    <ul className="plan-features">
                      <li>✓ Full member access</li>
                      <li>✓ Best for regulars</li>
                      <li>✓ Auto-reminder</li>
                    </ul>
                  </div>

                  <div 
                    className={`plan-card ${selectedPlan === 'threeMonth' ? 'selected' : ''}`}
                    onClick={() => setSelectedPlan('threeMonth')}
                  >
                    <div className="plan-header">
                      <div className="plan-icon">📆</div>
                      <div className="plan-name">3 Months</div>
                    </div>
                    <div className="plan-price">
                      {paymentCurrency === 'USD' ? '$339' : 
                       ethPrice ? `${(339 / ethPrice).toFixed(6)} ETH` : '...'
                      }
                    </div>
                    <div className="plan-desc">
                      {paymentCurrency === 'USD' ? '3-month bundle' : 
                       ethPrice ? `≈ $339 USD` : 'one-time'
                      }
                    </div>
                    <ul className="plan-features">
                      <li>✓ Discounted quarterly rate</li>
                      <li>✓ Priority coaching slots</li>
                      <li>✓ Merchandise bonus</li>
                    </ul>
                  </div>

                  <div 
                    className={`plan-card ${selectedPlan === 'custom' ? 'selected' : ''}`}
                    onClick={() => setSelectedPlan('custom')}
                    style={{ alignItems: 'flex-start' }}
                  >
                    <div className="plan-header">
                      <div className="plan-icon">🎯</div>
                      <div className="plan-name">Custom</div>
                    </div>
                    <div className="plan-price">
                      {paymentCurrency === 'USD' ? `$${customAmount}` : 
                       ethPrice ? `${(customAmount / ethPrice).toFixed(6)} ETH` : '...'
                      }
                    </div>
                    <div className="plan-desc">One-time custom amount</div>
                    <div style={{ marginTop: 8, width: '100%' }}>
                      <select value={customPreset} onChange={(e) => {
                        const v = e.target.value;
                        setCustomPreset(v);
                        if (v !== 'other') setCustomAmount(Number(v));
                      }} style={{ width: '100%', padding: '6px', borderRadius: 6 }}>
                        <option value="other">Enter custom amount…</option>
                        <option value="25">$25</option>
                        <option value="50">$50</option>
                        <option value="75">$75</option>
                        <option value="100">$100</option>
                        <option value="150">$150</option>
                        <option value="200">$200</option>
                      </select>
                      {customPreset === 'other' && (
                        <input type="number" min="1" value={customAmount} onChange={(e) => setCustomAmount(Number(e.target.value || 0))} style={{ width: '100%', padding: '6px', borderRadius: 6, marginTop: 8 }} />
                      )}
                    </div>
                  </div>
                </div>

                <button 
                  className="btn-pay-metamask"
                  onClick={async () => {
                    // Ensure wallet is connected/confirmed before opening payment modal
                    try {
                      await requestAndOpen();
                    } catch (e) { console.warn('requestAndOpen failed', e); }
                    setShowPaymentModal(true);
                  }}
                  disabled={!window.ethereum || loadingEthPrice}
                >
                  <span className="metamask-icon">🦊</span>
                  {(() => {
                    const amt = getPaymentAmount();
                    return `Pay ${paymentCurrency === 'USD' ? `$${amt.usd}` : `${amt.ethFormatted} ETH`} with MetaMask`;
                  })()}
                </button>
                
                {!window.ethereum && (
                  <p className="metamask-warning">
                    ⚠️ MetaMask not detected. <a href="https://metamask.io" target="_blank" rel="noopener noreferrer">Install MetaMask</a> to make payments.
                  </p>
                )}
              </div>

              {/* Payment History */}
              {paymentHistory.length > 0 && (
                <div className="payment-history">
                  <h4>Payment History</h4>
                  <div className="payment-list">
                    {paymentHistory.slice().reverse().map((payment, idx) => (
                      <div key={idx} className="payment-item">
                        <div className="payment-item-icon">
                          {payment.status === 'completed' ? '✓' : payment.status === 'pending' ? '⏳' : '❌'}
                        </div>
                        <div className="payment-item-details">
                          <div className="payment-item-amount">
                            ${payment.amountUSD || payment.amount}
                            {payment.amountETH && (
                              <span className="payment-eth-amount"> ({payment.amountETH} ETH)</span>
                            )}
                          </div>
                          <div className="payment-item-plan">{payment.plan}</div>
                          <div className="payment-item-date">
                            {new Date(payment.timestamp).toLocaleDateString()}
                          </div>
                        </div>
                        {payment.txHash && (
                          <a 
                            href={`https://etherscan.io/tx/${payment.txHash}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="payment-item-link"
                          >
                            View TX
                          </a>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Payment Reminder Info */}
              <div className="payment-reminder-info">
                <h4>📬 Payment Reminders</h4>
                <p>You'll receive automatic reminders every week if your payment is overdue.</p>
                <p>Enable browser notifications to stay updated.</p>
              </div>
            </div>
          )}

          {activeTab === 'occurring' && (
            <div className="tab-panel">
              {!showCheckoutForm ? (
                <OccurringPaymentsTab onOpenCheckout={(planId) => { setCheckoutPlan(planId); setShowCheckoutForm(true); }} />
              ) : (
                <div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                    <h3 style={{ margin: 0 }}>Secure Checkout</h3>
                    <button className="btn-sm" onClick={() => { setShowCheckoutForm(false); setCheckoutPlan(null); }}>Back</button>
                  </div>
                  <CheckoutForm returnUrlBase={window.location.origin} initialPlanId={checkoutPlan} planDefinitions={RECURRING_PLANS} />
                </div>
              )}
            </div>
          )}

          {activeTab === 'purchases' && (
            <div className="tab-panel">
              <StudentPurchaseHistory
                wallet={(selectedWalletForView || walletAddr)}
                student={student}
                orderHistory={Array.isArray(student?.orderHistory) ? student.orderHistory : []}
                onViewCollectable={(order) => {
                  try {
                    setActiveTab('store');
                    setShowStoreModal(true);
                    window.dispatchEvent(new CustomEvent('openCollectable', { detail: { order } }));
                  } catch (e) { console.warn('onViewCollectable failed', e); }
                }}
              />
            </div>
          )}

          {activeTab === 'collectables' && (
            <div className="tab-panel">
              {/* Desktop preview grid: show Pinata/IPFS preview thumbnails resolved from orders */}
              <div style={{ marginBottom: 12 }}>
                {loadingCollectablePreviews ? (
                  <div style={{ color: 'var(--portal-muted)' }}>Loading previews…</div>
                ) : collectablePreviews && collectablePreviews.length > 0 ? (
                  <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                    {collectablePreviews.slice(0, 8).map(p => (
                      <button key={p.orderId || p.title} onClick={() => { setSelectedWalletForView((selectedWalletForView || walletAddr)); setActiveTab('collectables'); }} style={{ border: 'none', padding: 0, background: 'none', cursor: 'pointer' }} title={p.title}>
                        <div style={{ width: 120, height: 120, borderRadius: 8, overflow: 'hidden', background: '#071322', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: p.minted ? '0 8px 20px rgba(124,58,237,0.16)' : 'none', border: p.minted ? '2px solid rgba(124,58,237,0.16)' : '1px solid rgba(255,255,255,0.04)' }}>
                          {p.imageUrl ? (
                            // image element with graceful fallback
                            <img src={p.imageUrl} alt={p.title} style={{ width: '100%', height: '100%', objectFit: 'cover' }} onError={(e) => { try { e.target.src = p.imageUrl.replace('gateway.pinata.cloud', 'ipfs.io'); } catch (er) {} }} />
                          ) : (
                            <div style={{ color: 'var(--portal-muted)', fontSize: 12, padding: 8, textAlign: 'center' }}>No preview</div>
                          )}
                        </div>
                        <div style={{ fontSize: 12, color: 'var(--portal-muted)', marginTop: 6, textAlign: 'center', width: 120, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{p.title}</div>
                      </button>
                    ))}
                  </div>
                ) : (
                  <div className="empty-state">No collectable previews available.</div>
                )}
              </div>

              <StudentCollectables wallet={(selectedWalletForView || walletAddr)} student={student} orderHistory={Array.isArray(student?.orderHistory) ? student.orderHistory : []} />
            </div>
          )}

          {activeTab === 'messages' && (
            <div className="tab-panel">
              <h3>Messages</h3>
              <div className="messages-container">
                <div className="messages-list">
                  {messages.length === 0 ? (
                    <p className="empty-state">No messages yet.</p>
                  ) : (
                    messages.slice().reverse().map((msg) => (
                      <div 
                        key={msg.id} 
                        className={`message-item ${msg.from === 'admin' ? 'from-admin' : 'from-student'}`}
                      >
                        <div className="message-header">
                          <strong>{msg.from === 'admin' ? 'Admin' : 'You'}</strong>
                          <span className="message-time">
                            {new Date(msg.timestamp).toLocaleString()}
                          </span>
                        </div>
                        <div className="message-body">{msg.body}</div>
                      </div>
                    ))
                  )}
                </div>
                <div className="message-compose">
                  <textarea
                    value={newMessage}
                    onChange={(e) => setNewMessage(e.target.value)}
                    placeholder="Type your message to the admin..."
                    rows={3}
                  />
                  <button
                    className="btn-primary"
                    onClick={sendMessage}
                    disabled={!newMessage.trim() || sendingMessage}
                  >
                    {sendingMessage ? 'Sending...' : 'Send Message'}
                  </button>
                </div>
                {isAdmin && (
                  <button
                    className="btn-sm admin-crown-btn"
                    title="Browse wallet-linked students"
                    onClick={() => { setShowWalletList(true); }}
                    style={{ marginLeft: 6 }}
                  >
                    👑
                  </button>
                )}
              </div>
            </div>
          )}

          {activeTab === 'flowchart' && (
            <div className="tab-panel">
              <h3>BJJ Technique Flowchart</h3>
              <div className="flowchart-info">
                <p>Visualize and customize your BJJ game. {editMode ? 'Drag nodes to reposition, click to edit.' : 'Click Edit to customize your flowchart.'}</p>
              </div>
              
              <div className="flowchart-view-tabs">
                <button 
                  className={`view-tab ${flowchartView === 'guard' ? 'active' : ''}`}
                  onClick={() => setFlowchartView('guard')}
                >
                  🛡️ Guard Game
                </button>
                <button 
                  className={`view-tab ${flowchartView === 'pass' ? 'active' : ''}`}
                  onClick={() => setFlowchartView('pass')}
                >
                  ⚡ Passing Game
                </button>
                <button 
                  className={`view-tab ${flowchartView === 'takedown' ? 'active' : ''}`}
                  onClick={() => setFlowchartView('takedown')}
                >
                  🥋 Takedowns
                </button>
                <div className="edit-controls">
                  {!editMode ? (
                    <button className="btn-sm btn-edit" onClick={() => setEditMode(true)}>
                      ✏️ Edit
                    </button>
                  ) : (
                    <>
                      <button className="btn-sm btn-success" onClick={saveFlowchart}>
                        💾 Save
                      </button>
                      <button className="btn-sm btn-cancel" onClick={cancelEdit}>
                        ✕ Cancel
                      </button>
                    </>
                  )}
                </div>
              </div>
              
              {editMode && (
                <div className="flowchart-toolbar" style={{ display: 'flex', gap: 8, alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap' }}>
                  <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                    <button className="btn-sm" onClick={() => addTechniqueNode('guard')}>+ Guard Position</button>
                    <button className="btn-sm" onClick={() => addTechniqueNode('pass')}>+ Guard Pass</button>
                    <button className="btn-sm" onClick={() => addTechniqueNode('takedown')}>+ Takedown</button>
                    <button className="btn-sm" onClick={() => addTechniqueNode('submission')}>+ Submission</button>
                    <button className="btn-sm btn-danger" onClick={resetFlowchart}>Reset to Default</button>
                  </div>
                  <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                    <button className="btn-sm" onClick={zoomOut}>−</button>
                    <div style={{ minWidth: 40, textAlign: 'center' }}>{flowZoom}x</div>
                    <button className="btn-sm" onClick={zoomIn}>+</button>
                    <button className="btn-sm" onClick={resetZoom}>Reset</button>
                    <div style={{ width: 1, height: 24, background: 'rgba(255,255,255,0.03)', margin: '0 8px' }} />
                    <button className="btn-sm" onClick={() => panBy(-60, 0)}>◀</button>
                    <button className="btn-sm" onClick={() => panBy(60, 0)}>▶</button>
                    <button className="btn-sm" onClick={() => panBy(0, -40)}>▲</button>
                    <button className="btn-sm" onClick={() => panBy(0, 40)}>▼</button>
                  </div>
                </div>
              )}

              <svg 
                className="flowchart-canvas" 
                viewBox="0 0 1000 700" 
                onMouseMove={handleMouseMove}
                onMouseUp={handleMouseUp}
                onMouseLeave={handleMouseUp}
                onTouchStart={handleTouchStart}
                onTouchMove={handleTouchMove}
                onTouchEnd={handleTouchEnd}
                style={{ touchAction: 'none' }}
              >
                <defs>
                  <marker id="arrowhead" markerWidth="10" markerHeight="10" refX="9" refY="3" orient="auto">
                    <polygon points="0 0, 10 3, 0 6" fill="rgba(255,255,255,0.6)" />
                  </marker>
                </defs>
                
                {/* Render edges */}
                <g transform={`translate(${flowPan.x},${flowPan.y}) scale(${flowZoom})`}>
                {flowchartData[flowchartView].edges.map((edge, idx) => {
                  const fromNode = flowchartData[flowchartView].nodes.find(n => n.id === edge.from);
                  const toNode = flowchartData[flowchartView].nodes.find(n => n.id === edge.to);
                  if (!fromNode || !toNode) return null;
                  
                  return (
                    <g key={idx}>
                      <line
                        x1={fromNode.x + 75}
                        y1={fromNode.y + 25}
                        x2={toNode.x + 75}
                        y2={toNode.y + 25}
                        stroke="rgba(255,255,255,0.4)"
                        strokeWidth="2"
                        markerEnd="url(#arrowhead)"
                      />
                      <text
                        x={(fromNode.x + toNode.x) / 2 + 75}
                        y={(fromNode.y + toNode.y) / 2 + 15}
                        fill="rgba(255,255,255,0.7)"
                        fontSize="11"
                        textAnchor="middle"
                      >
                        {edge.label}
                      </text>
                    </g>
                  );
                })}
                
                {/* Render nodes */}
                  {flowchartData[flowchartView].nodes.map(node => (
                      <g 
                        key={node.id}
                        className={`flowchart-node ${node.type} ${selectedNode?.id === node.id ? 'selected' : ''}`}
                        style={{ cursor: editMode ? 'grab' : 'default' }}
                        onMouseDown={(e) => editMode && handleNodeMouseDown(e, node)}
                        onTouchStart={(e) => editMode && handleTouchStartNode(e, node)}
                      >
                    <rect
                      x={node.x}
                      y={node.y}
                      width="150"
                      height="50"
                      rx="8"
                      fill={node.type === 'guard' ? 'rgba(59, 130, 246, 0.3)' : 
                            node.type === 'pass' ? 'rgba(168, 85, 247, 0.3)' : 
                            node.type === 'takedown' ? 'rgba(34, 197, 94, 0.3)' :
                            'rgba(239, 68, 68, 0.3)'}
                      stroke={selectedNode?.id === node.id ? '#fbbf24' : 'rgba(255,255,255,0.5)'}
                      strokeWidth={selectedNode?.id === node.id ? '3' : '2'}
                      onClick={() => editMode && handleNodeClick(node)}
                    />
                    <text
                      x={node.x + 75}
                      y={node.y + 30}
                      textAnchor="middle"
                      fill="white"
                      fontSize="14"
                      fontWeight="500"
                      pointerEvents="none"
                    >
                      {node.label}
                    </text>
                  </g>
                ))}
                </g>
              </svg>

              {selectedNode && editMode && (
                <div className="flowchart-editor">
                  <h4>Edit Technique</h4>
                  <div className="editor-field">
                    <label>Technique Name:</label>
                    <input
                      type="text"
                      value={selectedNode.label}
                      onChange={(e) => updateNodeLabel(selectedNode.id, e.target.value)}
                      placeholder="Enter technique name"
                    />
                  </div>
                  <div className="editor-field">
                    <label>Type:</label>
                    <span className={`type-badge ${selectedNode.type}`}>
                      {selectedNode.type === 'guard' ? '🛡️ Guard' : 
                       selectedNode.type === 'pass' ? '⚡ Pass' : 
                       selectedNode.type === 'takedown' ? '🥋 Takedown' : 
                       '🎯 Submission'}
                    </span>
                  </div>
                  <div className="editor-field">
                    <label>Connect to:</label>
                    <div className="connect-buttons">
                      {flowchartData[flowchartView].nodes
                        .filter(n => n.id !== selectedNode.id)
                        .map(n => (
                          <button
                            key={n.id}
                            className={`btn-xs ${flowchartData[flowchartView].edges.some(e => e.from === selectedNode.id && e.to === n.id) ? 'active' : ''}`}
                            onClick={() => toggleEdge(selectedNode.id, n.id)}
                          >
                            {n.label}
                          </button>
                        ))}
                    </div>
                  </div>
                  {selectedNode.editable && (
                    <button className="btn-danger btn-sm" onClick={() => deleteNode(selectedNode.id)}>
                      Delete Technique
                    </button>
                  )}
                </div>
              )}

              <div className="flowchart-legend">
                <div className="legend-item">
                  <span className="legend-color" style={{ background: 'rgba(59, 130, 246, 0.5)' }}></span>
                  <span>Guard Positions</span>
                </div>
                <div className="legend-item">
                  <span className="legend-color" style={{ background: 'rgba(168, 85, 247, 0.5)' }}></span>
                  <span>Guard Passes</span>
                </div>
                <div className="legend-item">
                  <span className="legend-color" style={{ background: 'rgba(34, 197, 94, 0.5)' }}></span>
                  <span>Takedowns</span>
                </div>
                <div className="legend-item">
                  <span className="legend-color" style={{ background: 'rgba(239, 68, 68, 0.5)' }}></span>
                  <span>Submissions</span>
                </div>
              </div>
            </div>
          )}
        </div>
        </>
        )}
      </div>

      {/* Profile Edit Modal */}
      {showEditModal && (
        <div className="modal-overlay" onClick={() => setShowEditModal(false)}>
          <div className="modal-content edit-profile-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>{isCreating ? '＋ Create Family Member' : '✏️ Edit Profile'}</h3>
              <button className="modal-close" onClick={() => setShowEditModal(false)}>×</button>
            </div>
            
            <div className="modal-body">
              <div className="form-grid">
                <div className="form-field">
                  <label>First Name</label>
                  <input
                    type="text"
                    value={editedProfile.firstName || ''}
                    onChange={(e) => setEditedProfile({ ...editedProfile, firstName: e.target.value })}
                  />
                </div>
                
                <div className="form-field">
                  <label>Last Name</label>
                  <input
                    type="text"
                    value={editedProfile.lastName || ''}
                    onChange={(e) => setEditedProfile({ ...editedProfile, lastName: e.target.value })}
                  />
                </div>
                
                <div className="form-field">
                  <label>Email</label>
                  <input
                    type="email"
                    value={editedProfile.email || ''}
                    onChange={(e) => setEditedProfile({ ...editedProfile, email: e.target.value })}
                  />
                </div>
                
                <div className="form-field">
                  <label>Phone</label>
                  <input
                    type="tel"
                    value={editedProfile.phone || ''}
                    onChange={(e) => setEditedProfile({ ...editedProfile, phone: e.target.value })}
                  />
                </div>
                
                <div className="form-field full-width">
                  <label>Address</label>
                  <input
                    type="text"
                    value={editedProfile.address || ''}
                    onChange={(e) => setEditedProfile({ ...editedProfile, address: e.target.value })}
                  />
                </div>
                
                <div className="form-field full-width">
                  <label>Emergency Contact</label>
                  <input
                    type="text"
                    value={editedProfile.emergencyContact || ''}
                    onChange={(e) => setEditedProfile({ ...editedProfile, emergencyContact: e.target.value })}
                    placeholder="Name and phone number"
                  />
                </div>
                
                <div className="form-field">
                  <label>Age Category</label>
                  <select value={editedProfile.ageCategory || 'adult'} onChange={(e) => setEditedProfile({ ...editedProfile, ageCategory: e.target.value })}>
                    <option value="adult">Adult</option>
                    <option value="kid">Kid</option>
                  </select>
                </div>

                <div className="form-field">
                  <label>Belt</label>
                  <select value={editedProfile.belt || ''} onChange={(e) => setEditedProfile({ ...editedProfile, belt: e.target.value })}>
                    {((editedProfile.ageCategory || student?.ageCategory) === 'kid') ? (
                      ['white','gray','yellow','orange','green'].map(b => <option key={b} value={b}>{b.charAt(0).toUpperCase() + b.slice(1)}</option>)
                    ) : (
                      ['white','blue','purple','brown','black'].map(b => <option key={b} value={b}>{b.charAt(0).toUpperCase() + b.slice(1)}</option>)
                    )}
                  </select>
                </div>
              </div>
            </div>
            
            <div className="modal-footer">
              <button 
                className="btn-cancel"
                onClick={() => setShowEditModal(false)}
              >
                Cancel
              </button>
              <button 
                className="btn-save"
                onClick={saveProfile}
                disabled={savingProfile}
              >
                {savingProfile ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Payment Modal */}
      {showPaymentModal && (
        <div className="modal-overlay" onClick={() => setShowPaymentModal(false)}>
          <div className="modal-content payment-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>🦊 MetaMask Payment</h3>
              <button className="modal-close" onClick={() => setShowPaymentModal(false)}>×</button>
            </div>
            
            <div className="modal-body">
              <div className="payment-confirmation">
                <div className="payment-plan-summary">
                  <div className="plan-icon-large">
                    {selectedPlan === 'weekly' ? '📅' : selectedPlan === 'biweekly' ? '💎' : selectedPlan === 'monthly' ? '🗓️' : selectedPlan === 'threeMonth' ? '📆' : '🎯'}
                  </div>
                  <h4>{selectedPlan === 'weekly' ? 'Weekly Plan' : selectedPlan === 'biweekly' ? 'Bi-Weekly Plan' : selectedPlan === 'monthly' ? 'Monthly Plan' : selectedPlan === 'threeMonth' ? '3-Month Bundle' : 'Custom Payment'}</h4>
                  <div className="payment-amount-large">
                    {(() => {
                      const amt = getPaymentAmount();
                      return paymentCurrency === 'USD' ? `$${amt.usd.toFixed(2)}` : `${amt.ethFormatted} ETH`;
                    })()}
                  </div>
                  <p className="payment-desc">
                    {selectedPlan === 'weekly' && 'Charged every week'}
                    {selectedPlan === 'biweekly' && 'Charged every 2 weeks'}
                    {selectedPlan === 'monthly' && 'Charged monthly'}
                    {selectedPlan === 'threeMonth' && 'One-time 3-month bundle'}
                    {selectedPlan === 'custom' && 'One-time custom amount'}
                  </p>
                </div>
                
                <div className="payment-details-box">
                  <div className="payment-detail-row">
                    <span>Student:</span>
                    <span>{student.name || `${student.firstName} ${student.lastName}`}</span>
                  </div>
                  <div className="payment-detail-row">
                    <span>Your Wallet:</span>
                    <span className="mono">{walletAddress?.slice(0, 8)}...{walletAddress?.slice(-6)}</span>
                  </div>
                  <div className="payment-detail-row">
                    <span>Gym Wallet:</span>
                    <span className="mono">{localStorage.getItem('gymWalletAddress') || localStorage.getItem('adminWallet') || '0x742d35...f0bEb'}</span>
                  </div>
                  <div className="payment-detail-row total">
                    <span>Total:</span>
                    <span>{(() => { const amt = getPaymentAmount(); return paymentCurrency === 'USD' ? `$${amt.usd.toFixed(2)} USD` : `${amt.ethFormatted} ETH (~$${amt.usd.toFixed(2)})`; })()}</span>
                  </div>
                </div>
                
                <div className="payment-info">
                  <p>✓ Secure blockchain transaction via MetaMask</p>
                  <p>✓ Automatic payment confirmation and receipts</p>
                  <p>✓ Reminders if your membership becomes overdue</p>
                </div>
              </div>
            </div>
            
            <div className="modal-footer">
              <button 
                className="btn-cancel"
                onClick={() => setShowPaymentModal(false)}
              >
                Cancel
              </button>
              <button 
                className="btn-pay"
                onClick={processPayment}
                disabled={processingPayment}
              >
                {processingPayment ? 'Processing...' : (() => { const amt = getPaymentAmount(); return paymentCurrency === 'USD' ? `Pay $${amt.usd.toFixed(2)}` : `Pay ${amt.ethFormatted} ETH`; })()}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );

}

// Helper function for default flowchart
function getDefaultFlowchartData() {
  return {
    guard: {
      nodes: [
        { id: 'closed-guard', label: 'Closed Guard', type: 'guard', x: 50, y: 100 },
        { id: 'open-guard', label: 'Open Guard', type: 'guard', x: 50, y: 200 },
        { id: 'half-guard', label: 'Half Guard', type: 'guard', x: 50, y: 300 },
        { id: 'butterfly', label: 'Butterfly Guard', type: 'guard', x: 50, y: 400 },
        { id: 'dlr', label: 'De La Riva', type: 'guard', x: 50, y: 500 },
        { id: 'armbar-guard', label: 'Armbar', type: 'submission', x: 450, y: 150 },
        { id: 'triangle', label: 'Triangle', type: 'submission', x: 450, y: 250 },
        { id: 'kimura-guard', label: 'Kimura', type: 'submission', x: 450, y: 350 },
        { id: 'sweep', label: 'Scissor Sweep', type: 'guard', x: 450, y: 450 }
      ],
      edges: [
        { from: 'closed-guard', to: 'armbar-guard', label: 'hip escape' },
        { from: 'closed-guard', to: 'triangle', label: 'control' },
        { from: 'half-guard', to: 'kimura-guard', label: 'underhook' },
        { from: 'butterfly', to: 'sweep', label: 'hook' }
      ]
    },
    pass: {
      nodes: [
        { id: 'knee-slice', label: 'Knee Slice', type: 'pass', x: 50, y: 100 },
        { id: 'toreando', label: 'Toreando', type: 'pass', x: 50, y: 200 },
        { id: 'leg-drag', label: 'Leg Drag', type: 'pass', x: 50, y: 300 },
        { id: 'stack-pass', label: 'Stack Pass', type: 'pass', x: 50, y: 400 },
        { id: 'pressure-pass', label: 'Pressure Pass', type: 'pass', x: 50, y: 500 },
        { id: 'side-control', label: 'Side Control', type: 'pass', x: 450, y: 200 },
        { id: 'mount', label: 'Mount', type: 'pass', x: 450, y: 350 },
        { id: 'armbar-pass', label: 'Armbar', type: 'submission', x: 750, y: 250 }
      ],
      edges: [
        { from: 'knee-slice', to: 'side-control', label: 'complete' },
        { from: 'toreando', to: 'side-control', label: 'complete' },
        { from: 'side-control', to: 'mount', label: 'transition' },
        { from: 'mount', to: 'armbar-pass', label: 'attack' }
      ]
    },
    takedown: {
      nodes: [
        { id: 'double-leg', label: 'Double Leg', type: 'takedown', x: 50, y: 100 },
        { id: 'single-leg', label: 'Single Leg', type: 'takedown', x: 50, y: 200 },
        { id: 'osoto-gari', label: 'Osoto Gari', type: 'takedown', x: 50, y: 300 },
        { id: 'seoi-nage', label: 'Seoi Nage', type: 'takedown', x: 50, y: 400 },
        { id: 'ankle-pick', label: 'Ankle Pick', type: 'takedown', x: 50, y: 500 },
        { id: 'side-control-td', label: 'Side Control', type: 'pass', x: 450, y: 200 },
        { id: 'mount-td', label: 'Mount', type: 'pass', x: 450, y: 350 },
        { id: 'guillotine', label: 'Guillotine', type: 'submission', x: 750, y: 250 }
      ],
      edges: [
        { from: 'double-leg', to: 'side-control-td', label: 'finish' },
        { from: 'single-leg', to: 'side-control-td', label: 'finish' },
        { from: 'osoto-gari', to: 'mount-td', label: 'finish' },
        { from: 'side-control-td', to: 'guillotine', label: 'transition' }
      ]
    }
  };
}

// Helper function for belt colors
function getBeltColor(belt) {
  const colors = {
    white: '#f0f0f0',
    blue: '#1e3a8a',
    purple: '#7c3aed',
    brown: '#78350f',
    black: '#0a0a0a'
  };
  return colors[belt] || '#f0f0f0';
}
